(ns graph-ui.web.core
  "Entry point for the graph-ui Web renderer (PLAN-graph-ui-v0-001-02).

   The Web renderer is the second renderer for the UI-DSL (ADR-graph-ui-v0-003), after the
   TUI (ui.render.tui). It consumes the same renderer-agnostic render tree from
   ui.interpret/render-tree and paints it into React DOM via reagent.

   Phase W4: connects to the bus server over WebSocket, dispatches :ui/attach
   to register on the gi island, receives the widget tree + source snapshot,
   resolves bindings, interprets into a render tree, and renders it live.
   Subscribes to :ui/source-changed for live updates."
  (:require [reagent.dom :as rdom]
            [reagent.core :as r]
            [graph-ui.web.lazy :as lazy]
            [clojure.walk :as walk]
            [clojure.string]
            ["@mantine/core" :refer [MantineProvider]]
            [graph-ui.web.render :as render]
            [graph-ui.web.interpret :as interpret]
            [graph-ui.web.bind :as bind]
            [graph-ui.web.theme :as theme]
            [graph-ui.web.version :as version]
            [graph-ui.web.activity :as activity]
            [graph-ui.web.busy :as busy]
            [graph-ui.web.nav :as nav]
            [graph-ui.web.islands :as islands]
            [graph-ui.web.tab :as tab]
            [graph-ui.web.ruler :as ruler]
            [graph-ui.web.responsive :as responsive]
            [graph-ui.web.origin :as origin]
            [graph-ui.web.note-image :as note-image]
            [graph-ui.web.xray :as xray]
            [graph-ui.web.bus-client :as bus-client]
            [graph-ui.web.widgets :as widgets]
            [graph-ui.web.dock-panes :as dock-panes]
            [graph-ui.web.support :as support]
            [graph-ui.web.reopen :as reopen]
            [graph-ui.web.stale :as stale]))

;; @feature F-gi-v3-010-01-graph-ui-poc
;; @adr ADR-graph-ui-v0-003-01-ui-from-formal-description

;; ---------------------------------------------------------------------------
;; App state — a single reagent atom holds everything the renderer needs.
;; ---------------------------------------------------------------------------

(def app-state
  "The renderer's working set:
   :status     — :connecting | :connected | :attached | :error | :disconnected
   :bus        — the BusClient instance (set on connect)
   :session-id — this renderer's UUID (generated once, sent on attach)
   :island-id  — the island we attached to (from :ui/attach reply)
   :widget-tree — the Actual-UI EDN tree (from :ui/attach reply, with :bind slots).
                  Named for what the server sends: a UI DECLARATION is what a
                  person writes, a WIDGET TREE is what it expands into (ea698c7,
                  2026-08-07). This client kept reading the retired :description
                  key for four days and rendered nothing — see on-attached.
   :sources    — {keyword value} source snapshot (from :ui/attach + :ui/read updates)
   :meta-ui-rules — the resolved Meta-UI rule-set for the attached island's own
                    :meta-ui-version (ADR-graph-ui-v0-004-01, from :ui/attach reply's
                    :meta-ui-rules) — never a hardcoded local copy
   :subscribed-sources — #{[source args] …} this tab has already dispatched
                    :ui/subscribe for. :ui/subscribe registers a brand-new
                    bus listener on EVERY call (ui.bind/subscribe has no
                    dedup) — re-subscribing to an already-bound source on
                    every :ui/widget-tree-changed (PLAN-graph-ui-v0-004-01
                    Phase 3) would leak one extra live listener per event,
                    so this set is how on-widget-tree-changed subscribes
                    only to sources a Composer edit just newly introduced.
   :error      — error message when :status is :error"
  (r/atom {:status        :connecting
           :bus           nil
           :session-id    nil
           :island-id     nil
           :widget-tree   nil
           :sources       {}
           :meta-ui-rules {}
           :subscribed-sources #{}
           :error         nil}))

;; ---------------------------------------------------------------------------
;; WS bus URL — graph-ui.web.origin owns the rule (PLAN-ikc-v3-021-01 phase 1).
;; The page and the socket have ONE origin, and the upgrade path is declared in
;; dsl/contexts/bus.edn. This ns had its own copy of that rule, and so did
;; graph-ui.web.studio. Both now read the one place.
;; ---------------------------------------------------------------------------

(defn fallback-uuid
  "Build a CANONICAL v4 UUID string by hand — the path taken when
  js/crypto.randomUUID is unavailable. That happens in a NON-SECURE context:
  crypto.randomUUID only exists over HTTPS or on localhost, so a client reached
  over plain http://<lan-ip>:4556 (the door of the shared bus-server)
  lands here.

  It MUST return a byte-for-byte canonical UUID (8-4-4-4-12, each group fully
  zero-padded). The previous version did not: its `s4` helper had four
  expressions but a fn returns only its LAST, so it emitted ONE hex digit where
  four were intended, minting malformed ids like \"53-3-4-8-4bc\". The client
  sent that as its session-id; JVM java.util.UUID/fromString is lenient and
  ZERO-PADDED it to \"00000053-0003-0004-0008-0000000004bc\" server-side, which
  the server then echoed on every :ui/source-changed. The client's own
  (unpadded) id no longer matched the (padded) id on its own events, so every
  session-scoped content refresh was silently dropped — the 2026-07-14
  'click a page, no content' bug. A canonical id closes it at the source: client
  and server now agree on the exact string everywhere (T-web-src-003)."
  []
  (let [hex (fn [] (.toString (rand-int 16) 16))
        seg (fn [n] (apply str (repeatedly n hex)))]
    ;; xxxxxxxx-xxxx-4xxx-Yxxx-xxxxxxxxxxxx  (Y ∈ 8|9|a|b — the v4 variant)
    (str (seg 8) "-" (seg 4) "-4" (seg 3) "-"
         (.toString (bit-or 8 (rand-int 4)) 16) (seg 3) "-" (seg 12))))

(defn gen-uuid
  "Generate a canonical v4 UUID, preferring the browser crypto API and falling
  back to fallback-uuid in non-secure contexts (plain http on a LAN IP, where
  crypto.randomUUID is undefined). Returns a cljs.core/uuid so pr-str encodes it
  as #uuid \"...\" for the server's :uuid schema.

  `crypto` is injectable (defaults to js/crypto) purely so the origin-coverage
  tests can drive BOTH branches — a secure-context stub with .randomUUID and a
  non-secure stub without — instead of mutating a read-only global (§2.8
  Testability)."
  ([] (gen-uuid js/crypto))
  ([crypto]
   (uuid
     (if (and crypto (.-randomUUID crypto))
       (.randomUUID crypto)
       (fallback-uuid)))))

;; ---------------------------------------------------------------------------
;; Attach lifecycle — connect, attach, subscribe, render.
;; ---------------------------------------------------------------------------

;; Debounced "rendered" activity marker. re-render! only swaps the tree; reagent
;; paints asynchronously, and a cascade calls re-render! many times. Coalesce to
;; ONE :activity-log entry, stamped after the next paint once re-renders settle —
;; so the panel answers "when did the island finish rendering?" (George, 2026-07-18).
(defonce ^:private render-mark-timer (atom nil))

(defn- mark-rendered! []
  (when-let [t @render-mark-timer] (js/clearTimeout t))
  (reset! render-mark-timer
    (js/setTimeout
      (fn [] (js/requestAnimationFrame
               ;; the STUDIO SHELL settling — distinct from island-view's
               ;; "preview rendered <island>", which marks the SELECTED island
               ;; actually painting in the live preview (what George wants to see).
               #(activity/record! {:kind :ui :label "studio render"})))
      120)))

;; WHAT :tiles-open SAID LAST TIME. nil until the first render.
;; (defonce takes no docstring in CLJS.)
(defonce ^:private last-tiles-open (atom nil))

;; DEFINED BELOW. watch-split! runs from re-render! and has to reach the
;; subscriber, which needs the render's own helpers and so comes later.
(declare subscribe-new-sources! on-source-changed)

(defn- watch-split!
  "SUBSCRIBE WHAT A NEWLY DRAWN PANE BINDS, when the tile list changes.

   THE READING AREA NO LONGER HAS TILES (2026-09-06). George said the readers
   have no tiles. So this watched a split that is gone, and what remains
   is the half that was never about splitting: the CONVERSATIONS island opens
   tiles, and a tile drawn for the first time needs its binds subscribed.

   ONE THING IS GONE WITH THE SPLIT: this used to call dock-panes/split-opened!
   to put the graph and the internals away when a second pane opened. There is
   no second pane to react to."
  [sources session-id]
  (let [now  (get sources (bind/snapshot-key :tile-columns {:session-id session-id}))
        was  @last-tiles-open]
    (when (number? now) (reset! last-tiles-open now))
    ;; ── AND SUBSCRIBE WHAT THE NEW PANE BINDS ──────────────────────────────
    ;;
    ;; subscribe-new-sources! runs when the WIDGET TREE changes. A split changes
    ;; neither the tree nor its binds — it changes $tile-sessions, the list the
    ;; renderer repeats the tree OVER. So the new pane's forty binds were never
    ;; subscribed, and its title stayed blank until the page was reloaded.
    ;;
    ;; ANY CHANGE, not only a split: closing a pane leaves subscriptions for a
    ;; session that no longer draws, which costs nothing, and re-opening one
    ;; needs them back.
    (when (and (number? was) (number? now) (not= was now))
      (let [{:keys [bus widget-tree]} @app-state]
        (when (and bus widget-tree)
          (subscribe-new-sources! bus widget-tree session-id))))))

(defn- re-render!
  "Resolve bindings with current sources, interpret, and swap the render tree
  into app-state. Called on initial attach and on every :ui/source-changed."
  []
  (let [{:keys [widget-tree sources session-id meta-ui-rules]} @app-state]
    (watch-split! sources session-id)
    (when widget-tree
      (let [;; THE PANE'S TILES GO INTO THE SUBSTITUTIONS, so a subtree with
            ;; :session-of resolves against a TILE's session and not this tab's
            ;; (ADR-graph-ui-v0-023-01). The value comes from the :tile-sessions
            ;; source, which is read like any other, so a pane with no tiles
            ;; supplies an empty vector and nothing changes.
            subst    {'$session-id session-id
                      bind/tile-sessions-symbol
                      (or (get sources (bind/snapshot-key
                                         :tile-sessions {:session-id session-id}))
                          [])
                      bind/focused-session-symbol
                      (or (get sources (bind/snapshot-key
                                         :tile-focus-session {:session-id session-id}))
                          "")}
            resolved (bind/resolve-bindings sources widget-tree subst)
            tree     (interpret/render-tree resolved meta-ui-rules)]
        (swap! app-state assoc :render-tree tree)
        (mark-rendered!)))))

(def canon-uuid
  "Normalise a UUID-ish value to canonical form. ONE implementation, and it lives
  in graph-ui.web.bind, because the snapshot KEY carries argument values and a
  key that spelled an id differently could not find its own value
  (ADR-graph-ui-v0-023-01). Kept as a name here so every existing caller and
  test reads unchanged."
  bind/canon-uuid)

(defn same-session?
  "True when two session ids denote the same session, comparing by CANONICAL
  UUID value so a zero-padding difference (client-minted vs server-echoed) never
  splits them — the 2026-07-14 'no content' bug (see on-source-changed)."
  [a b]
  (= (canon-uuid a) (canon-uuid b)))

;; ---------------------------------------------------------------------------
;; ONE ROUND TRIP FOR ONE CLICK (PLAN-ikc-v3-019-01 phase 4).
;;
;; A page click invalidates every source that declares :ui/selection-changed in
;; its :on, and the server announces each one separately. This client re-read
;; them ONE AT A TIME and re-rendered inside every reply callback. Measured
;; 2026-08-23 on the running deployment: 20 frames sent and 67 received for one
;; click, 16.6 KB in, and one page that answered 47.8 KB.
;;
;; The reads were never the cost. ui.bind/read answers a source in 0.2 ms at the
;; median, and the whole server side of a click is about 8 ms. The ROUND TRIPS
;; were the cost.
;;
;; So a changed source is now QUEUED, and the queue is flushed on the next tick
;; as one :ui/read-many. Everything that arrives in one reply is applied to
;; app-state together, and re-render! is called ONCE for the batch.
;;
;; WHY A TIMER OF 0 AND NOT A LONGER WINDOW. The announcements of one click all
;; arrive in the same task, so a zero-delay timeout already collects the whole
;; click. A longer window would only add latency to the case it is meant to help.
;; ---------------------------------------------------------------------------

(defonce ^:private pending-reads (atom {}))   ; {[source args] true}
(defonce ^:private flush-timer   (atom nil))

(defn flush-reads!
  "Send every queued (source, args) pair as ONE :ui/read-many, apply the values
   together, and re-render once.

   PUBLIC so a test can flush without waiting on the timer. A unit test that
   asserts what one :ui/source-changed causes must be able to run the whole
   reaction in one turn — the alternative is an async test whose failure mode is
   a timeout rather than a wrong value."
  [bus]
  (reset! flush-timer nil)
  (let [pairs (vec (keys (first (reset-vals! pending-reads {}))))]
    (when (seq pairs)
      (js-invoke bus "dispatch"
        {:type :ui/read-many
         :reads (mapv (fn [[source args]]
                        (cond-> {:source source} (seq args) (assoc :args args)))
                      pairs)}
        (fn [result]
          ;; ALREADY CLJS DATA. bus-client's edn-decode reads the frame with
          ;; cljs.reader, so a reply is a CLJS value and not a JS object — the
          ;; :ui/read path stores it into app-state untouched for the same reason.
          (let [values result
                ;; AN ERROR REPLY IS NOT DATA (PLAN-ikc-v3-018-01 phase 1), and
                ;; here it is per ENTRY: one refused source keeps its own last
                ;; value while the other eighteen are replaced.
                kept   (atom [])
                refused (atom [])]
            (if (bind/reply-failed? result)
              ;; The whole batch was refused. Every source keeps its last value.
              (js/console.warn "[ikc3] refused read-many, keeping every last value"
                               (pr-str {:n (count pairs)
                                        :reply (js->clj result :keywordize-keys true)}))
              (do
                (doseq [[i [source args]] (map-indexed vector pairs)]
                  (let [v (nth values i nil)]
                    (if (bind/reply-failed? v)
                      (swap! refused conj source)
                      (do (swap! kept conj source)
                          (swap! app-state assoc-in
                                 [:sources (bind/snapshot-key source args)] v)))))
                ;; ONE line for the whole batch, not one per source. The
                ;; per-source lines this replaces were a deliberate diagnostic
                ;; from the 2026-07-14 hunt (see on-source-changed), and they
                ;; answered the same three questions: did the events arrive, were
                ;; they for this session, and did the reads come back. They still
                ;; do, at 1 line for a click instead of 26.
                (js/console.log "[ikc3] read-many"
                                (pr-str {:asked (count pairs)
                                         :filled (count @kept)
                                         :refused @refused}))))
            (re-render!)))))))

(defn queue-read!
  "Queue one (source, args) pair and make sure a flush is scheduled."
  [bus source args]
  (swap! pending-reads assoc [source args] true)
  (when-not @flush-timer
    (reset! flush-timer (js/setTimeout #(flush-reads! bus) 0))))

(defn watch-lazy-binds!
  "Read what a widget asked for when it drew (PLAN-ikc-v3-033-01 step 4a).

   A bind that declares :load :on-visible is not in the attach snapshot, so the
   widget mounts with no value. graph-ui.web.lazy records the pair, this watch
   queues the read, and the queued reads go out as ONE :ui/read-many — the same
   batch a page click uses, for the same reason.

   IT SUBSCRIBES TOO, and only now. A subscription made at attach would announce
   a change for a value the client never had, and the client would then read it
   — which is exactly the read :on-visible exists to avoid."
  [bus session-id]
  (lazy/watch!
   (fn [pairs]
     (doseq [[source args] pairs]
       (lazy/taken! source args)
       (let [key [source args]]
         (when-not (contains? (:subscribed-sources @app-state) key)
           (swap! app-state update :subscribed-sources conj key)
           (js-invoke bus "dispatch" (cond-> {:type :ui/subscribe :source source}
                                       (seq args) (assoc :args args))
             (fn [evt]
               (when-let [src (:source evt)]
                 (on-source-changed bus {:source src :args args}))))))
       (queue-read! bus source args)))))

(defn- on-source-changed-one
  "ONE source of one :ui/source-changed announcement. re-read the changed source via :ui/read,
  update the sources map, and re-render.

  The bus broadcasts every event to every connected client, not just the
  session it's for — this handler runs in EVERY attached tab, including ones
  on a completely different island/session. For a session-scoped source
  (:args carries :session-id), only react when it matches THIS tab's own
  session-id; otherwise a click in one tab (e.g. notes) re-reads that
  source using the OTHER tab's session-id and pollutes this tab's own
  app-state with it. Session-independent sources (no :session-id in :args,
  e.g. :arch-roots) still refresh everywhere, unchanged.

  Session identity is compared BY VALUE (string). The 2026-07-14 'click a page,
  no content' bug lived UPSTREAM of this compare, in gen-uuid: over plain http
  (non-secure context) crypto.randomUUID is undefined, and the hand-rolled
  fallback minted a MALFORMED id (\"53-3-4-8-4bc\") that the server's lenient
  java.util.UUID/fromString zero-padded to \"00000053-…-04bc\" and echoed on
  every event — so this tab's own (unpadded) id and the (padded) id on its own
  events never string-matched, and every session-scoped refresh was silently
  dropped. Fixed at the source (fallback-uuid now returns a canonical id); this
  by-value compare is the correct identity check once both sides are canonical."
  [bus event]
  (let [source      (:source event)
        args        (:args event)
        ev-session  (or (:session-id args) (:session-id event))
        own-session (:session-id @app-state)
        ;; Only re-read a source this tab ACTUALLY subscribed to. The bus
        ;; broadcasts every source-changed to every tab; a session-INDEPENDENT
        ;; hot source (:bus-events fires on every domain event, formats 200
        ;; entries) would otherwise be re-read by EVERY tab, even ones that never
        ;; bind it — the storm the :activity-log surfaced 2026-07-18. A tab
        ;; subscribes to every source it binds (subscribe-new-sources!), so its
        ;; own :subscribed-sources IS the set of sources it should react to.
        subscribed? (contains? (set (map first (:subscribed-sources @app-state))) source)
        ;; A TILE'S SESSION IS ALSO THIS TAB'S BUSINESS (ADR-graph-ui-v0-023-01).
        ;;
        ;; The rule above predates tiles: react only when the event names THIS
        ;; TAB's session. A tile is a view of ANOTHER session, and this tab
        ;; subscribed for it — so an announcement for a tile's session was
        ;; dropped, and a pasted image appeared only after a page reload
        ;; (measured on the running deployment, 2026-08-23).
        ;;
        ;; THE SUBSCRIPTION IS THE ANSWER, exactly as it is for the key below: if
        ;; this tab asked about this source for that session, it wants to be told.
        ;; Nothing else changes — an event for a session this tab never asked
        ;; about is still skipped, which is what keeps one tab from re-reading
        ;; another's sources.
        subscribed-sessions
        (->> (:subscribed-sources @app-state)
             (filter #(= source (first %)))
             (keep #(:session-id (second %))))
        mine?       (or (nil? ev-session)
                        (same-session? own-session ev-session)
                        (boolean (some #(same-session? % ev-session)
                                       subscribed-sessions)))
        match?      (and subscribed? mine?)]
    ;; VISIBLE diagnostic (console.log, not debug) for the live 2026-07-14 hunt.
    ;; It triaged the whole space: if no line appeared on a page click the event
    ;; never reached this bundle, a "skip" line meant the session compare was
    ;; wrong, and "re-read" with an empty pane meant the fault was downstream.
    ;;
    ;; ONLY THE SKIPS ARE LOGGED PER EVENT NOW, and the three questions are still
    ;; all answerable. A click announces about 20 sources, so this line cost 20
    ;; pr-str calls on the hot path, measured 2026-08-23. The healthy case is
    ;; reported once for the whole batch by flush-reads! —
    ;; "[ikc3] read-many {:asked 19 :filled 19 :refused []}" — so absence of BOTH
    ;; lines still means the event never arrived, which is the property that
    ;; mattered. Observability over another guess (PRINCIPLE-ikc-v3-010).
    (when-not match?
      (js/console.log "[ikc3] source-changed skipped"
                      (pr-str {:source source
                               :verdict (if subscribed?
                                          :skip-other-session
                                          :skip-unsubscribed)
                               :own (str own-session) :event (str ev-session)})))
    (when source
      (if match?
        ;; THE READER OWNS THE KEY, not the event (ADR-graph-ui-v0-023-01).
        ;;
        ;; The event only says WHICH source changed. What to read, and what key to
        ;; store it under, comes from THIS tab's own subscriptions — which are
        ;; already (source, args) pairs built from its own binds. So one
        ;; source-changed refreshes every bind of that source, which is what a
        ;; reading pane of several tiles needs: `:page-title` changed, and the
        ;; two tiles that bind it for two different items both re-read.
        ;;
        ;; IT ALSO CLOSES A WHOLE CLASS OF SILENT BREAKAGE. Measured 2026-08-22:
        ;; several emitters send :args that do NOT match the bind's — notes-web
        ;; sends :page-outline with no :args at three sites and with an extra
        ;; :query at another, and studio-state puts :session-id at the top level
        ;; instead of inside :args. Keyed off the EVENT, each of those would write
        ;; a value under a key no bind ever reads, and the pane would quietly stop
        ;; refreshing. Keyed off the SUBSCRIPTION, an emitter's args are advisory
        ;; again — which is all they ever were before the key carried them.
        (let [pairs (->> (:subscribed-sources @app-state)
                         (filter #(= source (first %))))]
          ;; QUEUED, not dispatched (see flush-reads! above). Everything one
          ;; click invalidates goes out as one :ui/read-many, is applied
          ;; together, and paints once.
          (doseq [[_src sub-args] pairs]
            (queue-read! bus source sub-args)))
        ;; A genuine other-session event is the common, correct skip — already
        ;; logged above with both session values, so it can never again be an
        ;; invisible "no content" (PRINCIPLE-ikc-v3-010).
        nil))))

(defn on-source-changed
  "Handle a :ui/source-changed event.

   ONE EVENT MAY NAME SEVERAL SOURCES (PLAN-ikc-v3-019-01). ui.bind emits one
   announcement per session listing every source a bus event invalidated, instead
   of one event per source — about 40 frames for one page click became one. A
   handler that changed a single source still sends :source, because one source
   changed, so both shapes arrive here.

   The plural is unrolled into the singular rather than given a code path of its
   own: everything below it — the session match, the subscribed-source gate, the
   queue — must behave identically whichever shape carried the news."
  [bus event]
  (if-let [many (seq (:sources event))]
    (doseq [{:keys [source args]} many]
      (on-source-changed-one bus (-> event
                                     (assoc :source source :args args)
                                     (dissoc :sources))))
    (on-source-changed-one bus event)))

(defn- subscribe-new-sources!
  "Dispatch :ui/subscribe for every (source, args) pair `widget-tree` binds
  to that ISN'T already in app-state's :subscribed-sources — the shared
  helper on-attached and on-widget-tree-changed both use, so a widget
  created live by a Composer session gets exactly ONE :ui/subscribe call
  for its own source, never zero (missed) and never N (leaked duplicate
  listeners — ui.bind/subscribe has no dedup of its own)."
  [bus widget-tree session-id]
  (let [;; THE TILES GO IN, so a tile's binds are subscribed for the TILE's
        ;; session (ADR-graph-ui-v0-023-01). bound-nodes now substitutes, and it
        ;; honours each subtree's scope, so this function no longer does it.
        subst    {'$session-id session-id
                  bind/tile-sessions-symbol
                  (or (get (:sources @app-state)
                           (bind/snapshot-key :tile-sessions {:session-id session-id}))
                      [])
                  bind/focused-session-symbol
                  (or (get (:sources @app-state)
                           (bind/snapshot-key :tile-focus-session {:session-id session-id}))
                      "")}
        known    (:subscribed-sources @app-state)
        ;; THE SUBSTITUTION SOURCES ARE SUBSCRIBED EXPLICITLY, because no widget
        ;; binds them (ADR-graph-ui-v0-023-01). They feed $tile-sessions and
        ;; $focused-session, so a change to the tiles has to reach this tab —
        ;; and bound-nodes only finds what a widget binds.
        ;;
        ;; WITHOUT THIS the first attach drew the tiles correctly and the pane
        ;; never changed again: opening a tile refreshed :tile-set, which IS
        ;; bound, while the list the renderer repeats over stayed as it was.
        substitution-sources
        (for [src [:tile-sessions :tile-focus-session]]
          {:source src :args {:session-id session-id}})]
    (doseq [{:keys [source args]} (concat substitution-sources
                                          ;; A BIND THAT SAYS :on-visible IS NOT
                                          ;; SUBSCRIBED AND NOT READ HERE
                                          ;; (PLAN-ikc-v3-033-01 step 4a). This
                                          ;; function reads any pair it has no
                                          ;; value for, and the server omits a
                                          ;; lazy one from the snapshot — so
                                          ;; without this guard the client would
                                          ;; read every lazy source the moment
                                          ;; the island attached, and the whole
                                          ;; step would buy nothing. The widget
                                          ;; that draws it asks for it instead.
                                          (filter bind/at-attach?
                                                  (bind/bound-nodes widget-tree subst)))]
      (let [subst-args args
            key        [source subst-args]]
        ;; SUBSCRIBE ONLY WHAT THE SERVER DID NOT ALREADY REGISTER. :ui/attach
        ;; registers the whole bound set in-process and names it in the reply's
        ;; :subscriptions, which on-attached folds into :subscribed-sources —
        ;; so on a first attach this guard skips every pair and nothing
        ;; crosses the socket. A bind that appears LATER (a Composer edit, a
        ;; split) is not in the reply's set and still dispatches :ui/subscribe.
        (when-not (contains? known key)
          (swap! app-state update :subscribed-sources conj key)
          (js-invoke bus "dispatch" (cond-> {:type :ui/subscribe :source source}
                                     subst-args (assoc :args subst-args))
            (fn [evt]
              (when-let [src (:source evt)]
                (on-source-changed bus {:source src :args subst-args})))))
        ;; AND READ IT, WHEN WE HAVE NO VALUE FOR IT YET — whether or not the
        ;; pair was just subscribed.
        ;;
        ;; A subscription only says "tell me when this changes". On the first
        ;; attach that is enough, because :ui/attach's snapshot already carries
        ;; every value — except a source whose read answered nil, which the
        ;; snapshot omits; this guard catches those too. A pair that appears
        ;; LATER has no value and nothing to change it — so it stayed empty
        ;; until something unrelated moved.
        ;;
        ;; Measured 2026-09-06: a split opened the second reading pane and its
        ;; title stayed blank, while the server had the note in both tiles. The
        ;; new tile's session was subscribed and never read.
        ;;
        ;; ONLY WHEN ABSENT, so the first attach does not re-read the hundred
        ;; values it was just given.
        (when-not (contains? (:sources @app-state)
                             (bind/snapshot-key source subst-args))
          (queue-read! bus source subst-args))))))

(defn- on-widget-tree-changed
  "Handle a :ui/widget-tree-changed event (PLAN-graph-ui-v0-004-01 Phase 3):
  if it's for THIS tab's own attached island — a Composer session (or any
  other client) create/update/move/remove-widget-ing it — re-dispatch
  :ui/materialise for the current tree + Meta-UI rules, subscribe to
  whatever newly-bound sources the edit introduced, and re-render.
  Structural counterpart to on-source-changed: that reacts to bound DATA
  changing, this reacts to the tree's SHAPE changing."
  [bus event]
  (let [own-island (:island-id @app-state)]
    (when (and own-island (= own-island (:island-id event)))
      (js-invoke bus "dispatch" {:type :ui/materialise :island-id own-island}
        (fn [reply]
          ;; :ui/materialise answers {:widget-tree :meta-ui-rules}
          ;; (ui.frames/materialise-query) — NOT :description.
          (let [{:keys [widget-tree meta-ui-rules]} reply
                session-id (:session-id @app-state)]
            (swap! app-state assoc
                   :widget-tree   widget-tree
                   :meta-ui-rules (or meta-ui-rules {}))
            (subscribe-new-sources! bus widget-tree session-id)
            (re-render!)))))))

;; The URL/page-address and palette fns live below attach (they are the seam
;; attach drives); on-attached names them, so they are declared here.
(declare on-selection-changed page-addressable? apply-page-address!
         note-start-rule-event!)

(defn- on-attached
  "Handle the :ui/attach reply: store the widget tree + sources + resolved
  Meta-UI rules, subscribe to source-changed AND widget-tree-changed
  events, dispatch :ui/subscribe for each bound source so the server emits
  source-changed when the source's :on events fire, and render.

  THE KEY IS :widget-tree, and the cost of getting that wrong is the whole
  client. ea698c7 (2026-08-07) renamed the payload key :description ->
  :widget-tree across ui.attach/attach, ui.frames/materialise-query and the
  TUI, deliberately REMOVING the old name rather than aliasing it — and
  touched no CLJS at all. This handler went on destructuring :description,
  got nil, set :status :attached with a nil tree, and app's cond fell through
  to its :else arm: 'Connecting to bus…' on every island for four days, with
  a correct attach reply on the wire and zero console errors. The renamer's
  own principle ('a half-renamed vocabulary is worse than a consistently
  wrong one') is exactly right; nothing enforced it across the CLJ/CLJS seam."
  [bus reply]
  (let [{:keys [island-id widget-tree sources subscriptions meta-ui-rules server-build-id]} reply
        session-id (:session-id @app-state)
        ;; UI-version transparent contract (ADR-graph-ui-v0-010-01): the running
        ;; client compares its OWN baked build id to the one the server is
        ;; serving. Drift => a visible banner. "dev"/absent on either side means
        ;; "unstamped/unknown" — never cry stale on a dev build.
        stale?     (boolean (and server-build-id
                                 (not= "dev" version/build-id)
                                 (not= "dev" server-build-id)
                                 (not= version/build-id server-build-id)))]
    (swap! app-state
           assoc :status :attached
           :island-id island-id
           ;; THE PRESENTATION THIS TAB OWNS, so a detach closes this tab's
           ;; view and no other view of the same session (PLAN-ikc-v3-M4d-001-01
           ;; phase 8). A detach by :session-id alone closes the first match.
           :presentation-id (:presentation-id reply)
           :widget-tree widget-tree
           :sources (or sources {})
           :meta-ui-rules (or meta-ui-rules {})
           :stale-ui (when stale? {:client version/build-id :server server-build-id}))
    ;; THE PREVIOUS PAIR IS DROPPED FIRST (2026-09-08). This function runs again
    ;; on every re-attach — the top bar's session selector already did that, and
    ;; a reconnect after a bus restart now does it too. Each `subscribe` conj's
    ;; a fresh closure onto the client's subscription list and hands back the
    ;; only way to remove it, which was discarded — so a second attach made
    ;; every :ui/source-changed run its handler twice, a third three times, and
    ;; each one re-reads sources over the wire.
    (doseq [un (:chrome-unsubs @app-state)] (when (fn? un) (un)))
    (swap! app-state assoc :chrome-unsubs
           [(js-invoke bus "subscribe" :ui/source-changed #(on-source-changed bus %))
            (js-invoke bus "subscribe" :ui/widget-tree-changed #(on-widget-tree-changed bus %))
            ;; THE PAGE ADDRESS FOLLOWS THE SELECTION (row C-08). Not a
            ;; source-changed: the selection itself is the event, and every
            ;; selection path announces it.
            (js-invoke bus "subscribe" :ui/selection-changed on-selection-changed)])
    ;; :ui/attach REGISTERED every bound pair server-side and names them in
    ;; :subscriptions — the ~50 per-source :ui/subscribe dispatches this
    ;; replaced each came back with an announcement that queued a re-read of
    ;; a source the snapshot had JUST read. Mark them so the loop below
    ;; dispatches nothing for them and on-source-changed's subscribed filter
    ;; honours them; a bind that appears LATER still subscribes the old way.
    (swap! app-state update :subscribed-sources into
           (map (fn [{:keys [source args]}] [source args])) subscriptions)
    ;; A READING PANE IS WHAT MAKES THE ISLAND PAGE-ADDRESSABLE (rows
    ;; C-08/C-09/C-80): its bound :reading-page-id is the fact, read off the
    ;; widget tree rather than the island's name.
    ;; A FILTER CONDITION IN THE URL (IKC3/PLAN-ikc-v3-064-01/BOARD): an
    ;; island that binds :board-condition takes ?epic= and ?project=.
    (swap! app-state assoc :board-addressable?
           (boolean (some #(= :board-condition (:source %))
                          (bind/bound-nodes widget-tree {'$session-id session-id}))))
    (let [pageable? (page-addressable? widget-tree session-id)]
      (swap! app-state assoc :page-addressable? pageable?)
      (when pageable?
        ;; THE PALETTE'S OPEN STATE IS A SOURCE NO WIDGET BINDS — the overlay
        ;; is chrome, so chrome subscribes for it by name. Same subscribe +
        ;; read pairing subscribe-new-sources! uses, for the same reason: a
        ;; value must arrive, and a change must keep arriving.
        (let [args {:session-id session-id}
              key  [:command-palette-open args]]
          (when-not (contains? (:subscribed-sources @app-state) key)
            (swap! app-state update :subscribed-sources conj key)
            (js-invoke bus "dispatch"
                       {:type :ui/subscribe :source :command-palette-open
                        :args args}
                       (fn [evt]
                         (when-let [src (:source evt)]
                           (on-source-changed bus {:source src :args args}))))))
        (queue-read! bus :command-palette-open {:session-id session-id})))
    (subscribe-new-sources! bus widget-tree session-id)
    ;; A HIDDEN WIDGET ASKS FOR ITS OWN VALUE WHEN IT DRAWS (step 4a). The watch
    ;; is installed once per attach and replaces any earlier one, so a second
    ;; attach in this tab does not read everything twice.
    (watch-lazy-binds! bus session-id)
    (re-render!)))

(defn- detach!
  "Detach from the current island: dispatch :ui/detach, close the WS, reset state."
  []
  (let [{:keys [bus island-id session-id]} @app-state]
    (when (and bus island-id)
      (js-invoke bus "dispatch"
        {:type      :ui/detach
         :island-id island-id
         :session-id session-id}
        (fn [_]
          (js-invoke bus "close")
          (swap! app-state assoc
                 :status        :disconnected
                 :bus           nil
                 :island-id     nil
                 :widget-tree   nil
                 :sources       {}
                 :meta-ui-rules {}
                 :render-tree   nil))))))

(defn browse-key-from-path
  "PURE: the key of a work item that the path /browse/<key> names, in capital
   letters, or nil (IKC3/PLAN-ikc-v3-080-01/ITEM). The door answers every
   path with the bundle, so /browse/IKC-12 loads this page, and the island
   work-item opens the item."
  [pathname]
  (some-> (re-find #"^/browse/([A-Za-z][A-Za-z0-9]*-[0-9]+)/?$" (str pathname))
          second
          clojure.string/upper-case))

(defn- island-from-url
  "Which island to attach to — the ?island= query param, default \"gi\".
  One shared web client, any island: pass ?island=arch-explorer to attach
  to arch-explorer instead. The path /browse/<key> is the island work-item."
  []
  (or (some-> (js/URLSearchParams. (.-search js/location)) (.get "island"))
      (when (browse-key-from-path (.-pathname js/location)) "work-item")
      "gi"))

(defn- follow-browse-link!
  "Dispatch :ui/open-work-item for the key of /browse/<key>, at each attach,
   so a new session of the page shows the same item. The tab of the browser
   names the key, as in Jira."
  [bus session]
  (when-let [k (browse-key-from-path (.-pathname js/location))]
    (set! (.-title js/document) k)
    (js-invoke bus "dispatch"
      {:type :ui/open-work-item :session-id session :key k}
      (fn [reply]
        (when (:error reply)
          (js/console.warn "the work item of the address did not open:" (pr-str reply)))))))

(defn- instrument-from-url
  "?instrument=1 — stamp every rendered node with `data-widget-id`.

  This is the seam the fidelity check measures through
  (F-graph-ui-v0-008-01): the browser rendering is the REFERENCE a Penpot board
  is judged against, and both sides already carry :widget-id (the board as
  plugin data `ikc3/widget-id`), so keying the DOM by it turns 'does the board
  look like the island?' from an eye judgement into a join. Opt-in via URL so
  the ordinary render is untouched — same reasoning as render/render-tree's own
  `instrument?` default."
  []
  (= "1" (some-> (js/URLSearchParams. (.-search js/location)) (.get "instrument"))))

(defn session-list-request
  ;; Public for the same reason show-resumed-banner? is: this is a DECISION rule
  ;; about which rows a person is offered, and it deserves a test that needs
  ;; no browser (T-web-picker-001).
  "PURE: the :ui/list-sessions request the top-bar picker sends.

  THIS ISLAND'S SESSIONS ONLY (George, 2026-09-06). Without :island-id the
  picker listed every session on the deployment, and a label is eight hex
  characters — so two unrelated sessions read alike and one was picked for
  the other. :island-id is nil until the attach reply lands, and nil means no
  filter, which is the right answer before there is an island.

  OMITTED, NOT NIL, when there is no island yet. The declaration says
  [:island-id {:optional true} :uuid] — optional and NOT [:maybe :uuid] — so an
  explicit nil is refused where an absent key means \"every island\".

  ARCHIVED SESSIONS ONLY ON REQUEST (PLAN-ikc-v3-047-01, defect D2 of
  SPEC-alatus-v0-003-02). Until 2026-09-27 this request ALWAYS carried
  :include-archived? true. So a session a person archived stayed in the picker,
  and 'Archive it' seemed to do nothing. The declared query already excludes
  archived sessions by default, and the per-island selector already sent the
  default. Now the top bar agrees with both. `show-archived?` is the person's
  'show archived' option, and it is the only way the archived rows come back.

  OLDER SESSIONS ONLY ON REQUEST TOO (PLAN-ikc-v3-050-01). The declared query
  shows the 7 newest sessions of the island and every session younger than 7
  days (George, 2026-09-27). `show-older?` is the 'show older' option. It
  writes nothing: the hidden sessions were never archived."
  ([island-id show-archived?]
   (session-list-request island-id show-archived? false))
  ([island-id show-archived? show-older?]
   (cond-> {:type :ui/list-sessions}
     show-archived? (assoc :include-archived? true)
     show-older?    (assoc :include-older? true)
     island-id      (assoc :island-id island-id))))

(defn picker-groups
  ;; Public for the reason session-list-request is (T-web-picker-001).
  "PURE: the rows of the top-bar picker, from a :ui/list-sessions reply.

  {:self <this tab's own entry, or nil> :open [...] :archived [...]}. The
  tab's own session is never in either group — it is the selected option.

  The archived group is EMPTY unless `show-archived?`. The request already
  leaves archived sessions out, so this is the second half of one rule: a reply
  that arrives after the person hid the archived rows must not draw them."
  [sessions session-id show-archived?]
  (let [self   (first (filter #(= (:value %) (str session-id)) sessions))
        others (remove #(= (:value %) (str session-id)) sessions)
        {archived true open false} (group-by #(boolean (:archived? %)) others)]
    {:self     self
     :open     (vec open)
     :archived (if show-archived? (vec archived) [])}))

(defn- refresh-sessions!
  "Pull the open sessions (:ui/list-sessions) into app-state for the top-bar
  selector. Includes IDLE sessions — ones with no live surface at all — which
  is why the server derives them from :ui-session frames rather than from the
  presentations (2026-07-19). Archived sessions come only when the person
  asked for them (session-list-request)."
  []
  (when-let [bus (:bus @app-state)]
    (js-invoke bus "dispatch"
      (session-list-request (:island-id @app-state) (:show-archived? @app-state)
                            (:show-older? @app-state))
      (fn [sessions] (swap! app-state assoc :sessions (vec sessions))))))

(defn- refresh-whoami!
  "Ask the bus WHO IS ON THE OTHER SIDE and keep the answer in app-state.

   George, 2026-09-13, after the sign-in page landed: add the who am I
   indication in the top bar.

   :authz/whoami IS A DECLARED QUERY and it answers for the caller only, so it
   needs no capability — its own docstring says a page must be able to ask
   before it knows. The CONNECTION wins over any payload, so the answer is the
   person this socket was authenticated as at the upgrade and never a person a
   client named.

   ONCE PER CONNECTION IS ENOUGH. The principal is established at the handshake
   and is immutable for the connection's life (ADR-ikc-v3-065-01 decision 5), so
   there is nothing to poll: signing in or out changes it only at the next
   reload, which is a new connection and a new ask."
  []
  (when-let [bus (:bus @app-state)]
    (js-invoke bus "dispatch" {:type :authz/whoami}
               (fn [r] (swap! app-state assoc :whoami (js->clj r :keywordize-keys true))))))

(defn who-label
  "PURE: what the top bar says about who is on the other side.

   nil when the answer has not arrived — a top bar must not claim `nobody`
   while it is still asking, which is the same rule waiting-banner keeps."
  [whoami]
  (when (map? whoami)
    (if (:signed-in? whoami)
      (str (:handle whoami))
      "not signed in")))

(defn waiting-banner
  "PURE. What the screen says while there is no render tree yet.

   {:headline s :detail s :warn s-or-nil} for a status, an island name and the
   seconds the current :ui/attach has been outstanding.

   IT IS A FUNCTION SO IT CAN BE TESTED, and it is tested because this text has
   now been wrong twice for the same reason — a catch-all arm reports the FIRST
   state it covers unless it is made to say otherwise. T-web-banner-001.

   THE RULE: never name a layer that is known to be working. :connected means
   the socket is open, so the socket must not be the thing the sentence
   accuses."
  [status island secs]
  (cond
    (= status :attached)
    {:headline "Attached, but there is no render tree"
     :detail   (str "the :ui/attach reply carried no usable :widget-tree — "
                    "island " (or island "?")
                    ", check on-attached and the reply's keys")}

    (= status :connected)
    {:headline (str "Loading " (or island "the island") "…"
                    (when (and secs (pos? secs)) (str " " secs "s")))
     :detail   (str "the bus is connected — waiting for its :ui/attach reply. "
                    "A large island takes several seconds to answer.")
     :warn     (when (and secs (>= secs 20))
                 (str "This is longer than any island should take. The socket "
                      "is open, so the reply is the thing to look at, not the "
                      "connection."))}

    :else
    {:headline "Connecting to bus…"
     :detail   (str "status " (pr-str status))}))

;; How long the CURRENT :ui/attach has been unanswered, in whole seconds, or
;; nil when nothing is outstanding. (defonce takes no docstring in CLJS.)
;;
;; A WAIT MUST LOOK LIKE A WAIT AND NOT LIKE A HANG. Measured 2026-09-01:
;; reader-v2's attach answered in 10.2 s and conversations' in 13.4 s, against
;; 1.2 s for studio. For all of that time the screen said one motionless
;; sentence, so a slow island and a broken one were indistinguishable — and
;; George read the slow one as broken, correctly, because nothing on the
;; screen said otherwise.
(defonce ^:private waiting (r/atom nil))

(defonce ^:private waiting-timer (atom nil))

(defn- stop-waiting! []
  (when-let [t @waiting-timer] (js/clearInterval t))
  (reset! waiting-timer nil)
  (reset! waiting nil))

(defn- start-waiting! []
  (stop-waiting!)
  (let [t0 (js/Date.now)]
    (reset! waiting 0)
    (reset! waiting-timer
            (js/setInterval #(reset! waiting (quot (- (js/Date.now) t0) 1000)) 500))))

(defn tree-from-search
  "The tree a Journal link names — the ?tree= of a location's search string —
   or nil (M4c-AC-16, 2026-09-24). Public and PURE, so a test reads the rule
   without a browser.

   A daily note's Journal line links `?island=tot&tree=<id>`. The island param
   picks the island, as for every link, and this one says which tree of it to
   open. The server decides what the id means, so no shape is checked here."
  [search]
  (when (exists? js/URLSearchParams)
    (some-> (js/URLSearchParams. (str search)) (.get "tree") str not-empty)))

(defonce ^:private tree-link-followed? (atom false))

(defn- follow-tree-link!
  "Dispatch :ui/open-thought-tree for the ?tree= of this page, ONCE per page
   load. A re-attach after a reconnect must not open the linked tree again
   over a tree the person opened since."
  [bus session]
  (when-let [tid (and (not @tree-link-followed?) (tree-from-search (.-search js/location)))]
    (reset! tree-link-followed? true)
    (js-invoke bus "dispatch"
      {:type :ui/open-thought-tree :session-id session :tree-id tid}
      (fn [reply]
        (when (:error reply)
          (js/console.warn "the linked tree did not open:" (pr-str reply)))))))

(def board-link-conditions
  "The URL parameters that set a condition of the board filter, in the order
   they are dispatched (IKC3/PLAN-ikc-v3-064-01/BOARD). The island epics
   links a row to /index.html?island=board&epic=<uuid>."
  ["project" "epic"])

(defn board-conditions-from-search
  "PURE: [[condition value] …] for each parameter of board-link-conditions
   that the search string names with a value, or an empty vector. The server
   checks each value (:ui/choose-board-condition), so no shape is checked
   here."
  [search]
  (if (exists? js/URLSearchParams)
    (let [ps (js/URLSearchParams. (str search))]
      (vec (for [c board-link-conditions
                 :let [v (some-> (.get ps c) str not-empty)]
                 :when v]
             [c v])))
    []))

(defonce ^:private board-link-followed? (atom false))

(defn- follow-board-link!
  "Dispatch :ui/choose-board-condition for each ?project= and ?epic= of this
   page, ONCE per page load, on an island that binds :board-condition. A
   re-attach after a reconnect must not set the link's filter again over a
   filter the person chose since. A value that the server refuses is the
   sentence on the status line of the board."
  [bus session]
  (when (and (:board-addressable? @app-state) (not @board-link-followed?))
    (when-let [cs (seq (board-conditions-from-search (.-search js/location)))]
      (reset! board-link-followed? true)
      (doseq [[c v] cs]
        (js-invoke bus "dispatch"
          {:type :ui/choose-board-condition :session-id session :condition c :value v}
          (fn [reply]
            (when (:error reply)
              (js/console.warn "the board condition in the URL was not set:" (pr-str reply)))))))))

;; ---------------------------------------------------------------------------
;; The open page's address — ?page=<id> (SPEC-alatus-v0-002-02 rows C-08/C-09,
;; lane AL-N). ikc1 put the page in the URL, so a link, a reload and the
;; browser's own Back all name the same thing; this file owns that seam.
;; ---------------------------------------------------------------------------

(defn page-from-search
  "PURE: the ?page= of a location's search string, or nil — the same shape as
   tree-from-search, so the rule is testable without a browser."
  [search]
  (when (exists? js/URLSearchParams)
    (some-> (js/URLSearchParams. (str search)) (.get "page") str not-empty)))

(defn- page-addressable?
  "Does the attached island have a READING PANE for the address to name —
   decided by the widget tree, not by the island's name: any tree that binds
   :reading-page-id is one the ?page= of the URL means something to. An island
   that gains a reader gains the address with it; one without never writes
   the parameter."
  [widget-tree session-id]
  (boolean
    (some #(= :reading-page-id (:source %))
          (bind/bound-nodes widget-tree {'$session-id session-id}))))

(defn- write-page-to-url!
  "Keep `?page=` telling the truth about the page this session reads.

   EVERY OTHER PARAMETER IS KEPT — ?island= and ?ws= came in on this URL and
   dropping one changes what a reload attaches to.

   A FIRST ?page= is a replaceState, not a push: the person never chose to
   arrive 'nowhere', so annotating the entry they are on must not manufacture
   a step Back could land on. A page-to-page CHANGE pushes, because that is a
   navigation Back should undo. An id the address already says writes
   nothing — that equality is also what keeps popstate's select-page from
   echoing one more history entry back."
  [page-id]
  (when (exists? js/URLSearchParams)
    (let [params (js/URLSearchParams. (.-search js/location))
          have   (.get params "page")
          want   (some-> page-id str not-empty)]
      (cond
        (= have want) nil
        (nil? want)
        (do (.delete params "page")
            (let [s (str (.toString params))]
              (.replaceState js/history nil ""
                             (str (.-pathname js/location)
                                  (when (seq s) (str "?" s))
                                  (.-hash js/location)))))
        (nil? have)
        (do (.set params "page" want)
            (.replaceState js/history nil ""
                           (str (.-pathname js/location) "?" (.toString params)
                                (.-hash js/location))))
        :else
        (do (.set params "page" want)
            (.pushState js/history nil ""
                        (str (.-pathname js/location) "?" (.toString params)
                             (.-hash js/location))))))))

(defn- on-selection-changed
  "The server says this session's reading moved — write the address bar to
   match (row C-08). Every path that selects — a tree row, a wikilink, a
   palette pick, a nav arrow — funnels through this one event, so the URL
   needs no watcher of its own.

   ONLY THIS TAB'S SESSION. The event is broadcast to every tab, and a tile's
   session selection is not this tab's address — same-session? is the guard
   the 2026-07-14 id bug made load-bearing.

   ONLY A PAGE HAS A PAGE ADDRESS. The same event announces an outline node
   (:kind :outline-node) — a Leo node is not a page, and its id in ?page=
   would be a dead link — and a deselection (:target nil). Both drop the
   parameter; the address names the page the pane reads or it names none."
  [event]
  (note-start-rule-event! event)
  ;; THE TRAIL OF THE BACK ARROW FOLLOWS THE SAME EVENT (fault report
  ;; e776e38b). A daily note opens through :ui/goto-daily-note, which is not a
  ;; :ui/select-page, so only this event tells the trail that the reader is
  ;; there. graph-ui.web.nav/arrived! says why.
  (when (same-session? (:session-id event) (:session-id @app-state))
    (nav/arrived! event))
  (when (and (:page-addressable? @app-state)
             (same-session? (:session-id event) (:session-id @app-state)))
    (write-page-to-url! (when (= :page (:kind event)) (:target event)))))

(defn- on-popstate
  "The browser's own Back/Forward landed on another ?page= — follow it
   (row C-08). The selection event the resulting select-page emits writes
   nothing, because the address already says it — the loop closes on the
   equality inside write-page-to-url!."
  [_]
  (when (:page-addressable? @app-state)
    (when-let [pid (page-from-search (.-search js/location))]
      (let [{:keys [bus session-id]} @app-state]
        (when (and bus session-id)
          (js-invoke bus "dispatch"
                     {:type :ui/select-page :session-id session-id :page-id pid}
                     (fn [_] nil)))))))

;; ONE START RULE PER SESSION PER PAGE LOAD (row C-09). A re-attach of the
;; SAME session — a lost socket — must not re-impose the URL over a page the
;; person opened since (the reason tree-link-followed? exists). A DIFFERENT
;; session is a new start, and the rule applies again: the "＋ new session"
;; pick re-attaches, and a session that stores no page takes today's daily.
;; The atom holds the session the rule last ran for; a page load resets it,
;; an attach of another session does not.
(defonce ^:private page-address-applied-to (atom nil))

;; A RULE STILL DECIDING ENDS ON THE SESSION'S FIRST SELECTION. Arms two and
;; three answer over a round trip, and in that window the person — or the
;; session's other live tab — can have opened a page already. The atom holds
;; {:session sid} while the rule travels; the first :ui/selection-changed of
;; that session ends it, so a stale arm cannot yank the pane back to a page
;; the person already left. The rule's own select counts as the landing —
;; its reply then has nothing left to do.
(defonce ^:private start-rule-state (atom nil))

(defn- start-rule-live?
  "Is the start rule for `session` still travelling — no selection of that
   session has landed yet? A pending arm checks this before it acts."
  [session]
  (same-session? (:session @start-rule-state) session))

(def ^:private focus-echo-grace-ms
  "How long after the start rule ends that caret reports of the session stay
   muted. The stale outline's autofocus echo lands AFTER the first selection
   event — measured 2026-09-29: ~400–500 ms behind it — so unmuting at the
   event lets it through and focus-frame yanks the pane back."
  1500)

(defn- end-start-rule!
  "End the rule and release the focus mute a grace later: the stale render's
   autofocus is still on its way when the landing event arrives. A new rule
   for the same session keeps its own mute — the timer checks."
  [session]
  (reset! start-rule-state nil)
  (js/setTimeout
    (fn []
      (when-not (start-rule-live? session)
        (widgets/unmute-focus! session)))
    focus-echo-grace-ms))

(defn- note-start-rule-event!
  "End a still-travelling start rule on the session's first selection —
   the rule's own arm landing, or the person's earlier pick winning."
  [event]
  (when (same-session? (:session @start-rule-state) (:session-id event))
    (end-start-rule! (:session @start-rule-state))))

(defn- apply-page-address!
  "The start-page rule, once per session per page load, in C-09's order:

   1. The URL's ?page= wins — a link means what it says.
   2. Else the session's stored page, which attach's snapshot already
      answered through :reading-page-id — only the address bar needs
      catching up (replaceState; the arrival was not a navigation).
   3. Else today's daily note — ikc1's home — via :ui/goto-daily-note,
      which resolves-creates it and selects it the usual way."
  [bus session]
  (letfn [(stored-or-daily
            ;; ASK, DON'T READ THE SNAPSHOT. A bind inside a hidden subtree is
            ;; deferred (:load :on-visible) — on a phone the reading pane can
            ;; be exactly that at attach — so the snapshot may hold nothing
            ;; for a session that DOES have a page, and arm 3 would open the
            ;; daily over the page the session was reading.
            ;;
            ;; THE STORED PAGE IS NOT RE-SELECTED. The session already holds
            ;; it — that is what the read answers — so the pane is right and
            ;; only the address bar needs catching up. Dispatching a select
            ;; here adds a write that can land on a session the tab has
            ;; already left (a "＋ new session" pick does not wait for this
            ;; read) and yank the pane of its next owner.
            []
            (js-invoke bus "dispatch"
                       {:type :ui/read :source :reading-page-id
                        :args {:session-id session}}
                       (fn [current]
                         (when (start-rule-live? session)
                           (end-start-rule! session)
                           (if (seq (str current))
                             ;; NO SELECT, SO NO EVENT. The trail must
                             ;; still hold the page the tab opened on, or
                             ;; Back from the first link skips it.
                             (do (nav/record! current)
                                 (write-page-to-url! current))
                             (js-invoke bus "dispatch"
                                        {:type :ui/goto-daily-note
                                         :session-id session :offset 0}
                                        (fn [_] nil)))))))]
    (when (and (:page-addressable? @app-state)
               (not= session @page-address-applied-to))
      (reset! page-address-applied-to session)
      (let [pid (page-from-search (.-search js/location))]
        (reset! start-rule-state {:session session})
        ;; The attach snapshot still draws the session's OLD page, and its
        ;; outline's autofocus would report that stale caret as a focus pick —
        ;; focus-frame then pulls the pane back to it. Caret reports of this
        ;; session are echoes until the rule lands; widgets/mute-focus! says why.
        (widgets/mute-focus! session)
        (if pid
          (js-invoke bus "dispatch"
                     {:type :ui/select-page :session-id session :page-id pid}
                     (fn [reply]
                       (when (start-rule-live? session)
                         (end-start-rule! session)
                         ;; A ?page= that does not resolve is a dead link:
                         ;; fall back to the session's own start rule rather
                         ;; than leave the pane on whatever the adopted
                         ;; session had open.
                         (if (:error reply)
                           (do (js/console.warn "the page in the URL did not open:"
                                                (pr-str reply))
                               (reset! start-rule-state {:session session})
                               (stored-or-daily))
                           (write-page-to-url! pid)))))
          (stored-or-daily))))))

;; ---------------------------------------------------------------------------
;; The command palette — Ctrl+K, the whole corpus in one box
;; (SPEC-alatus-v0-002-02 row C-80, lane AL-N).
;;
;; OPEN/CLOSED IS THE SESSION'S FACT, not this component's: the declared
;; :command-palette-open source holds it, :ui/open-command-palette and
;; :ui/close-command-palette are its only writers, and the key, the top-bar
;; button and a scripted dispatch all take the one path. The box's text, the
;; rows and the cursor are this overlay's own — they are not state anyone
;; else can disagree about.
;; ---------------------------------------------------------------------------

(def ^:private palette-row-limit
  "How many hits the palette draws — the plan's 'up to eight rows'."
  8)

(def ^:private palette-debounce-ms
  "How long a pause in typing lasts before the palette asks again — one
   bounded query each time the person STOPS to type, not one per keystroke."
  150)

(defonce ^:private palette
  (r/atom {:query "" :rows [] :cursor 0 :timer nil :request 0}))

(defn- palette-open?
  "Whether this session's palette is open — read off the subscribed source,
   so a close dispatched by anything (another surface of the session, a
   script) is read here the same way."
  []
  (boolean
    (get (:sources @app-state)
         (bind/snapshot-key :command-palette-open
                            {:session-id (:session-id @app-state)}))))

(declare palette-search!)

(defn- palette-set-open!
  "Dispatch the declared open/close command — the only writer of the state
   this overlay reads back. Closing also drops the box's text and rows: a
   palette reopens fresh, as ikc1's did."
  [open?]
  (let [{:keys [bus session-id]} @app-state]
    (when (and bus session-id)
      (js-invoke bus "dispatch"
                 {:type       (if open? :ui/open-command-palette :ui/close-command-palette)
                  :session-id session-id}
                 (fn [_] nil))
      (when-not open?
        (when-let [t (:timer @palette)] (js/clearTimeout t))
        (reset! palette {:query "" :rows [] :cursor 0 :timer nil
                         :request (:request @palette)}))
      (when open?
        (swap! palette assoc :query "" :rows [] :cursor 0)
        ;; The empty box asks for the corpus's first rows at once — the
        ;; palette is never drawn empty while the corpus has pages.
        (palette-search! bus session-id "")))))

(defn- palette-search!
  "Ask :page-search-results for `text` — ONE bounded query, issued here and
   only here. :query is sent even when blank, which is how the same source
   reads 'the first rows' for the palette while the sidebar's absent :query
   still means 'the stored filter' (see the source's own :args).
   :request makes the LAST answer win: an earlier reply landing after a
   later one would otherwise draw stale rows under the box's current text."
  [bus session-id text]
  (let [n (:request (swap! palette update :request inc))]
    (js-invoke bus "dispatch"
               {:type   :ui/read
                :source :page-search-results
                :args   {:session-id session-id :query (str text)
                         :limit palette-row-limit}}
               (fn [rows]
                 ;; A REFUSED READ IS NOT A ROW SET. bind/reply-failed? is
                 ;; the check flush-reads! keeps for the same reason.
                 (when (and (= n (:request @palette))
                            (not (bind/reply-failed? rows)))
                   (swap! palette assoc :rows (vec (or rows []))
                          :cursor 0))))))

(defn- palette-on-input
  "A keystroke in the box: hold it a moment, then ask. The person typing
   fast costs nothing until they pause — the debounce the plan's 'each time
   the person stops to type' names."
  [bus session-id text]
  (swap! palette assoc :query text)
  (when-let [t (:timer @palette)] (js/clearTimeout t))
  (swap! palette assoc :timer
         (js/setTimeout #(palette-search! bus session-id text)
                        palette-debounce-ms)))

(defn- palette-create!
  "The palette's 'create' row: a page titled exactly what was typed. The
   draft form is the declared path (:ui/update-draft → :ui/create-note) —
   the same one the notes island's New Note takes — and the created page is
   then selected, which the palette's pick promises."
  [bus session-id title]
  (js-invoke bus "dispatch"
             {:type :ui/update-draft :session-id session-id
              :field :title :value title}
             (fn [_]
               (js-invoke bus "dispatch"
                          {:type :ui/create-note :session-id session-id}
                          (fn [reply]
                            (let [ev  (if (vector? reply) (first reply) reply)
                                  pid (:page-id ev)]
                              (when pid
                                (js-invoke bus "dispatch"
                                           {:type :ui/select-page
                                            :session-id session-id
                                            :page-id pid}
                                           (fn [_] nil)))))))))

(defn palette-rows
  ;; Public for the same reason session-list-request is: a decision rule,
  ;; tested without a browser (T-web-page-*).
  "PURE: the rows the palette draws — the hits, then the 'create' row when
   the box names no page exactly (the plan's '\"create\" when no title
   matches'). Each row is {:kind :page|:create :label :value}."
  [{:keys [query rows]}]
  (let [q       (clojure.string/trim (str query))
        exact?  (some #(= (clojure.string/lower-case (:label % ""))
                          (clojure.string/lower-case q))
                      rows)
        hits    (mapv (fn [{:keys [label value]}]
                        {:kind :page :label label :value value})
                      rows)]
    (if (and (seq q) (not exact?))
      (conj hits {:kind :create :label (str "Create \"" q "\"") :value nil})
      hits)))

(defn- palette-activate!
  "Choosing a row — Enter on the cursor's row, or a click: select the page,
   or create it for the :create row. Either way the palette closes, because
   the ask was answered."
  [bus session-id row]
  (case (:kind row)
    :page   (when (:value row)
              (js-invoke bus "dispatch"
                         {:type :ui/select-page :session-id session-id
                          :page-id (:value row)}
                         (fn [_] nil)))
    :create (palette-create! bus session-id (:query @palette))
    nil)
  (palette-set-open! false))

(defn- palette-key!
  "Keys inside the palette's own box: arrows move the cursor over the drawn
   rows (the create row included), Enter takes the cursor's row. Escape is
   handled by the window listener, so it works from any focus."
  [rows e]
  (let [{:keys [bus session-id]} @app-state
        n (count rows)]
    (case (.-key e)
      "ArrowDown" (do (.preventDefault e)
                      (when (pos? n)
                        (swap! palette update :cursor #(mod (inc %) n))))
      "ArrowUp"   (do (.preventDefault e)
                      (when (pos? n)
                        (swap! palette update :cursor #(mod (dec %) n))))
      "Enter"     (do (.preventDefault e)
                      (when-let [row (get rows (:cursor @palette))]
                        (palette-activate! bus session-id row)))
      nil)))

(defn- command-palette
  "The palette overlay — chrome, not island content, mounted beside
   xray/overlay. Open/closed is the session's :command-palette-open source;
   everything in the box is this component's own atom."
  []
  (when (palette-open?)
    (let [{:keys [query rows cursor]} @palette
          {:keys [bus session-id]}   @app-state
          drawn (palette-rows @palette)]
      [:<>
       ;; THE BACKDROP IS THE CLICK-AWAY CLOSE — the same dismissal shape the
       ;; island picker uses, under the dialog on the named layers.
       [:div {:style    {:position "fixed" :inset 0
                         :zIndex "var(--layer-overlay)"
                         :background "rgba(0,0,0,0.35)"}
              :on-click #(palette-set-open! false)}]
       [:div {:role "dialog" :aria-label "Search pages"
              :data-import-id "command-palette"
              :style {:position "fixed" :top "15vh" :left "50%"
                      :transform "translateX(-50%)"
                      ;; 92vw so a 390px phone still sees the whole box.
                      :width "min(560px, 92vw)"
                      :zIndex "var(--layer-modal)"
                      :background "var(--panel)"
                      :border "1px solid var(--border)"
                      :borderRadius "8px"
                      :boxShadow "0 12px 32px rgba(0,0,0,0.4)"
                      :overflow "hidden"}}
        [:input {:type "text" :value query
                 :data-import-id "command-palette-input"
                 :placeholder "Search pages — type any part of a name"
                 :aria-label "Search pages"
                 :auto-focus true
                 ;; AND .focus(): autoFocus alone is enough on first mount,
                 ;; but the input survives the open flag flipping while React
                 ;; keeps the node — focus where the open lands it.
                 :ref (fn [el] (when el (.focus el)))
                 :on-change   (fn [e]
                                (palette-on-input bus session-id
                                                  (.. e -target -value)))
                 :on-key-down (partial palette-key! drawn)
                 :style {:display "block" :width "100%"
                         :padding "10px 14px" :fontSize "15px"
                         :background "transparent" :color "var(--text)"
                         :border "none" :outline "none"
                         :borderBottom "1px solid var(--border)"}}]
        (into [:div {:role "listbox"}]
              (map-indexed
                (fn [i {:keys [kind label]}]
                  [:div {:role "option" :aria-selected (str (= i cursor))
                         :data-import-id (if (= kind :create)
                                           "command-palette-create"
                                           "command-palette-row")
                         :on-click #(palette-activate! bus session-id (nth drawn i))
                         :on-mouse-enter #(swap! palette assoc :cursor i)
                         :style {:padding "8px 14px" :cursor "pointer"
                                 :fontSize "14px" :whiteSpace "nowrap"
                                 :overflow "hidden" :textOverflow "ellipsis"
                                 :color (if (= kind :create) "var(--accent)" "var(--text)")
                                 :background (if (= i cursor)
                                               "var(--hover)" "transparent")}}
                   label])
                drawn))]])))

(defn- on-window-keydown
  "The palette's two global keys. Ctrl/Cmd+K toggles, Escape closes — both
   read the open state off the session's source, so a palette another surface
   opened still answers this tab's Escape."
  [e]
  (let [k (.-key e)]
    (cond
      (and (or (.-ctrlKey e) (.-metaKey e))
           (= "k" (clojure.string/lower-case (str k))))
      (do (.preventDefault e)
          (when (:page-addressable? @app-state)
            (palette-set-open! (not (palette-open?)))))

      (and (= "Escape" k) (palette-open?))
      (palette-set-open! false))))

;; THE PAGE ADDRESS AND THE PALETTE NEED TWO WINDOW LISTENERS, ONCE for the
;; life of the bundle — install-time, not attach-time, so a re-attach can
;; never double them. Guarded on js/window: the :test build runs this
;; namespace in Node, which has none.
(defonce ^:private window-listeners-installed?
  (boolean
    (when (exists? js/window)
      (.addEventListener js/window "popstate" on-popstate)
      (.addEventListener js/window "keydown" on-window-keydown)
      true)))

(declare show-resumed-banner?)

(defn attach-request
  ;; Public for the same reason show-resumed-banner? is: a decision, tested
  ;; without a browser.
  "The :ui/attach command of this tab, for island `island` under `session`.

  `resume?` true asks the SERVER to resume the idle session of the island, and
  to use `session` only when none is idle (George, 2026-09-28: \"Second tab
  starts new session if it does not have its idle session\"). The first attach
  of a tab asks it. A re-attach after a lost socket, or a pick in the selector,
  names its session and does not.

  THE CHOICE IS THE SERVER'S SINCE 2026-09-28 (PLAN-ikc-v3-050-01). The tab
  read the list of sessions and then attached, so two tabs that opened at the
  same moment both adopted one idle session. ui.attach/resumable-session picks
  and attaches in one step now.

  :client-session-raw is the string form of `session`, so the server can flag
  a non-canonical session id at the attach seam (see ui.attach/attach 4c)."
  [island session resume?]
  (cond-> {:type               :ui/attach
           :island-title       island
           :mode               :canvas
           :session-id         session
           :client-build-id    version/build-id
           :client-session-raw (str session)}
    resume? (assoc :resume-idle? true)))

(defn tab-session-key
  "The sessionStorage key that holds the session THIS TAB shows, per island.
   sessionStorage belongs to one tab: a reload keeps it, and a new tab starts
   with none."
  [island]
  (str "ikc3:tab-session:" island))

(defn first-attach
  ;; Public for the same reason attach-request is: a decision, tested without
  ;; a browser.
  "PURE. The session and the resume flag of the FIRST attach of this page, as
   `[session resume?]`.

   A RELOAD RETURNS TO ITS OWN SESSION (2026-09-29). George, 2026-09-28:
   \"still reload of a tab does not retain its panes state and proportion\".
   A reload minted a new uuid and asked the server to resume the idle session
   of the island. The server picks the LAST USED idle one, so another tab's
   session could win, and the tab's own session was often not idle yet: its
   old socket closes a moment after the new page attaches. Then the tab got a
   new session with no arrangement, and the default drew.

   So a reload attaches BY NAME to the session it stored, with no resume.
   `nav-type` is the navigation type of the page (\"reload\", \"navigate\",
   \"back_forward\"). Only \"reload\" reuses the stored id. A duplicated tab
   copies sessionStorage too, and it must not share the session of the tab it
   was copied from. `stored` is the stored string or nil, and `fresh` is a new
   uuid for every other case."
  [nav-type stored fresh]
  (let [sid (when (and (= "reload" nav-type) (string? stored)
                       (re-matches #"(?i)[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}"
                                   stored))
              (uuid stored))]
    (if sid [sid false] [fresh true])))

(defn- navigation-type
  "How this page was reached, from the Navigation Timing API. nil where the
   browser does not say."
  []
  (try
    (some-> (.getEntriesByType js/performance "navigation") (aget 0) .-type)
    (catch :default _ nil)))

(defn- stored-tab-session
  [island]
  (try (.getItem js/sessionStorage (tab-session-key island))
       (catch :default _ nil)))

(defn- store-tab-session!
  "Remember the session this tab shows. A browser that refuses storage loses
   only this memory: the next reload resumes as before."
  [island session]
  (try (.setItem js/sessionStorage (tab-session-key island) (str session))
       (catch :default _ nil)))

(defn- attach-with-session!
  "Dispatch :ui/attach for this tab's island under `session`. Extracted from
  connect-and-attach! so the top-bar selector can RE-attach under a different
  session: joining another surface's session is literally 'attach again under
  its id', after which both tabs are live windows onto the one shared session.

  With `resume?` the server may answer with ANOTHER session, the idle one it
  resumed. The tab then adopts the :session-id of the reply, and the resume
  banner states it when show-resumed-banner? says so."
  ([bus session] (attach-with-session! bus session false))
  ([bus session resume?]
   (start-waiting!)
   (swap! app-state assoc :session-id session)
   (js-invoke bus "dispatch"
     (attach-request (island-from-url) session resume?)
     (fn [reply]
       (js/console.log "attach reply:" (pr-str reply))
       (stop-waiting!)
       (if (:error reply)
         (swap! app-state assoc :status :error
                :error (str "attach failed: " reply))
         (let [session (or (:session-id reply) session)]
           (swap! app-state assoc :session-id session)
           (store-tab-session! (island-from-url) session)
           (when-let [r (:resumed reply)]
             (when (show-resumed-banner? r (js/Date.now))
               (swap! app-state assoc :resumed r)))
           (on-attached bus reply)
           (follow-tree-link! bus session)
           (follow-board-link! bus session)
           (follow-browse-link! bus session)
           ;; THE START-PAGE RULE, once per session per page load (row C-09):
           ;; the URL's ?page=, else the session's last page, else today's
           ;; daily. A session SWITCH re-attaches and the rule applies again —
           ;; that is how a fresh session reaches its daily note.
           (apply-page-address! bus session)
           (refresh-sessions!)
           ;; ONCE, HERE, for the reason refresh-whoami!'s own docstring gives:
           ;; the principal is fixed at the handshake, so there is nothing to
           ;; poll. This is the first moment the socket is known to work.
           (refresh-whoami!)))))))

(defn- switch-session!
  "Join session `sid`, or mint a fresh one when `sid` is nil. Detaches this
  tab's current presentation FIRST (sequenced in the callback, not racing it),
  then re-attaches under the chosen session.

  :subscribed-sources is cleared because every entry is keyed with args
  carrying the OLD session-id — kept, they would never match an event again
  and the tab would silently stop re-reading its own sources."
  [sid]
  (let [{:keys [bus island-id session-id presentation-id]} @app-state]
    (when bus
      (let [go   (fn [new-sid]
                   ;; Any deliberate switch answers the resume question, so the
                   ;; banner has nothing left to ask.
                   (swap! app-state dissoc :resumed)
                   (swap! app-state assoc :subscribed-sources #{})
                   ;; The ?page= the address bar carries names the page of the
                   ;; session this tab is LEAVING. A switch is a new start
                   ;; (C-09): keep it and the new session's start rule would
                   ;; reopen the page the old one was reading.
                   (when (:page-addressable? @app-state)
                     (write-page-to-url! nil))
                   (attach-with-session! bus new-sid))
            then (fn [] (if sid
                          (go sid)
                          (js-invoke bus "dispatch" {:type :ui/open-session}
                                     (fn [reply] (go (:session-id reply))))))]
        (if island-id
          (js-invoke bus "dispatch"
                     (cond-> {:type :ui/detach :island-id island-id :session-id session-id}
                       presentation-id (assoc :presentation-id presentation-id))
                     (fn [_] (then)))
          (then))))))

(defn- archive-session!
  "Put this tab's session away (:ui/archive-session) and open a fresh one.
  NOT a destruction (2026-07-19): the session keeps everything, including its
  agent conversation, and picking it from the archived group restores it. The
  server force-detaches every surface rendering it — including this tab — so
  re-attaching under a new session is what stops us painting one that has been
  put away.

  `confirm?` is false when the caller's gesture ALREADY said archive — the
  resume banner's 'Archive it' button names the session it is archiving, so a
  second dialog asking the same question is noise, not safety."
  ([] (archive-session! true))
  ([confirm?]
   (let [{:keys [bus session-id]} @app-state]
     (when (and bus session-id
                (or (not confirm?)
                    (js/confirm (str "Archive this session?\n\n"
                                     "Every surface showing it detaches and it leaves the "
                                     "session list. Nothing is lost — it keeps its history "
                                     "and conversation. To restore it, pick 'show archived' "
                                     "in this selector, then pick the session."))))
       (js-invoke bus "dispatch" {:type :ui/archive-session :session-id session-id}
                  (fn [_] (switch-session! nil)))))))

(def resumed-banner-min-idle-ms
  "How long a session must be idle before its resume is ANNOUNCED (George,
  2026-09-27: \"show it only after a 15 minutes idle\"). A reload closes the
  tab's own presentation a moment before the new page lists sessions, so every
  reload resumed a session idle for 0s and showed the banner for nothing."
  (* 15 60 1000))

(defn show-resumed-banner?
  ;; Public for the same reason attach-request is: a decision rule, tested
  ;; without a browser.
  "True when the resume of `session` must be stated in resumed-banner. Only the
  BANNER waits for the threshold — the tab adopts the session either way, so a
  reload keeps its place silently. A session with no :idle-since has an unknown
  age, and an unknown age is stated rather than hidden."
  [session now-ms]
  (let [since (:idle-since session)]
    (or (nil? since)
        (>= (- now-ms since) resumed-banner-min-idle-ms))))

(defn- connect-and-attach!
  "Connect to the WS bus, then dispatch :ui/attach for island-from-url —
  RESUMING an idle session for this island when there is one.

  The resume happens before any uuid is minted, on purpose: minting a fresh
  session on every load is exactly what made sessions disposable, and George's
  model says an idle session should be picked up again rather than stranded.
  The adoption is never silent — resumed-banner offers the three-way choice
  (keep it / start new instead / archive it)."
  []
  (try
    (let [bus (bus-client/make-bus-client (origin/page-bus-url))]
      (swap! app-state assoc :bus bus)
      ;; A note's pasted images are read through this bus (IKC3-Bugs-20260928
      ;; item 9). markdown draws them and knows no bus, so it is handed over here.
      (note-image/use-bus! bus)
      ;; THE SOCKET CAME BACK; THE SESSION DID NOT (George, 2026-09-08: "Web
      ;; page does not reconnect with the server after bus restart - it does not
      ;; function until is reloaded"). bus-client re-opens the socket on its
      ;; own now, but a restarted bus holds no presentation for this browser, so
      ;; the page would be connected to a server that has never heard of it.
      ;;
      ;; RE-ATTACH UNDER THE SAME SESSION ID, so the reader keeps their place —
      ;; the selected page, the tiles, the arrangement are all slots on that
      ;; session frame and it outlives the socket.
      ;;
      ;; :subscribed-sources IS CLEARED FIRST, and that is the half that is easy
      ;; to miss. It is the client's memory of which (source, args) pairs it has
      ;; already asked the server to watch, and it exists to stop duplicate
      ;; :ui/subscribe calls. After a restart the SERVER remembers none of them,
      ;; so that memory has become a lie that would keep every source silent.
      (js-invoke bus "on-reopen"
        (fn []
          (swap! app-state assoc :subscribed-sources #{} :status :connected)
          (attach-with-session! bus (or (:session-id @app-state) (gen-uuid)))))
      (js-invoke bus "ready"
              (fn []
                (swap! app-state assoc :status :connected)
                ;; A RELOAD RETURNS TO THE SESSION THIS TAB STORED. Otherwise
                ;; THE SERVER RESUMES THE IDLE SESSION, or uses this new uuid
                ;; when none is idle (first-attach and attach-request say why).
                (let [island (island-from-url)
                      [sid resume?] (first-attach (navigation-type)
                                                  (stored-tab-session island)
                                                  (gen-uuid))]
                  (attach-with-session! bus sid resume?)))))
    (catch js/Error e
      (js/console.error "connect-and-attach! error:" e)
      (swap! app-state assoc :status :error
             :error (str "connect error: " (.-message e))))))

;; ---------------------------------------------------------------------------
;; Reagent components — the UI.
;; ---------------------------------------------------------------------------

(defn- handle-prompt-submit
  "On Enter in the input bar, dispatch :agent/prompt to the gi agent.

  Includes :focus-path when the code-context pane currently has a file open
  (read from the same :sources map render/render-tree already fills from —
  see :code-context-path in dsl/contexts/ui.edn's :sources level) so the
  agent can call its own :read tool on the file the user means by 'this
  file', without gi ever pushing the file's content into the conversation
  itself."
  [bus session-id buf sending err e]
  (when (and (= 13 (.-keyCode e))
             (seq @buf)
             bus
             (not @sending))
    (reset! err nil)
    (.preventDefault e)
    (let [text       @buf
          ;; :code-context-path is bound WITH :args in
          ;; dsl/instances/arch-explorer/arch-explorer-ui.edn, so its snapshot
          ;; entry is keyed by the pair (ADR-graph-ui-v0-023-01). Read with the
          ;; bare keyword this was nil, and the prompt silently sent no
          ;; :focus-path. Found by the inventory of every reader, not by the
          ;; symptom.
          focus-path (get-in @app-state
                             [:sources (bind/snapshot-key
                                         :code-context-path
                                         {:session-id (:session-id @app-state)})])]
      ;; THE WORDS STAY UNTIL THE BUS ACCEPTS THEM (STALL, never VANISH). Until
      ;; 2026-09-20 the buffer was emptied BEFORE the dispatch, so a refusal or a
      ;; lost connection took the prompt with it. widgets/refusal-of reads the
      ;; reply; on a refusal the text stands and :sending drops, so Enter can
      ;; send it again.
      (reset! sending true)
      (js-invoke bus "dispatch"
        ;; STOPGAP (2026-07-22): the chat agent is hardcoded here. Swapped from
        ;; crews/gi-coding-agent to crews/antigravity-coder to drive the chat on
        ;; the Antigravity backend. The real fix is an agent picker bound to
        ;; :agent-frames (list-frames :agent) whose choice fills this :frame-ref
        ;; — :agent/prompt already honours ANY frame-ref, so no engine change is
        ;; needed. Two behaviour notes vs gi: crews/antigravity-coder is
        ;; READ-ONLY (authority #{:read}, enforced — it cannot write/run
        ;; commands), and it is self-contained (runs its own tools, so the chat's
        ;; tool-activity view won't show ikc3 :tools/execute steps).
        (cond-> {:type      :agent/prompt
                 :agent-id  session-id
                 :frame-ref "crews/antigravity-coder"
                 :input     text}
          (seq focus-path) (assoc :focus-path focus-path))
        (fn [reply]
          (reset! sending false)
          ;; THE REASON IS SHOWN, as widget-input shows it (PR #40 review):
          ;; words kept in silence would leave the person guessing.
          (if-let [why (widgets/refusal-of reply)]
            (reset! err why)
            (reset! buf "")))))))

(defn- save-conversation!
  "Dispatch :conversation/save-as-note to persist the current conversation
  as a note in the gi corpus. Prompts the user for a title."
  [bus session-id]
  (when (and bus session-id)
    (let [title (js/prompt "Save conversation as note — title:")]
      (when (and title (seq (.trim title)))
        (js-invoke bus "dispatch"
          {:type            :conversation/save-as-note
           :conversation-id session-id
           :corpus          "gi"
           :title           (.trim title)}
          (fn [reply]
            (let [ev (if (vector? reply) (first reply) reply)]
              (cond
                (:error ev)
                (js/alert (str "Save failed: " (:reason ev)))
                (= (:type ev) :conversation/error)
                (js/alert (str "Save failed: " (:reason ev)))
                (= (:type ev) :conversation/saved-as-note)
                (js/alert (str "Saved as note: " (:title ev)))
                :else
                (js/alert (str "Unexpected reply: " (pr-str reply)))))))))))

(defn- open-file!
  "Dispatch :ui/open-file to load a source file into the :code-context pane,
  scoped to this view's own session (Phase 8: the source is keyed per
  session so simultaneous views don't clobber each other's code-context).
  Prompts the user for a file path."
  [bus]
  (when bus
    (let [path (js/prompt "Open file — path:")]
      (when (and path (seq (.trim path)))
        (js-invoke bus "dispatch"
          {:type :ui/open-file :path (.trim path) :session-id (:session-id @app-state)}
          (fn [reply]
            (let [ev (if (vector? reply) (first reply) reply)]
              (cond
                (= (:type ev) :ui/open-file-error)
                (js/alert (str "Open failed: " (:reason ev)))
                :else nil))))))))

(defn- close-file!
  "Dispatch :ui/close-file to clear this view's :code-context pane — the
  counterpart to open-file!. Without it a file stays 'focused' forever:
  handle-prompt-submit would keep attaching its path as :focus-path on every
  future prompt, even long after the conversation moved on."
  [bus]
  (when bus
    (js-invoke bus "dispatch"
      {:type :ui/close-file :session-id (:session-id @app-state)}
      (fn [_] nil))))

(defn- input-bar
  "A prompt input bar at the bottom of the screen. On Enter, dispatches
  :agent/prompt to the gi agent via the bus. Includes a Save button to
  persist the conversation as a note."
  []
  (let [{:keys [bus session-id]} @app-state
        buf     (r/atom "")
        sending (r/atom false)
        ;; THE REFUSAL, shown beside the prompt until the next keystroke
        ;; (STALL, never VANISH, PR #40).
        err     (r/atom nil)]
    (fn []
      [:div.flex.items-center.gap-2.px-3.py-2.border-t.ikc-bd-border.ikc-bg-panel.shrink-0
       [:span.ikc-fg-faint.text-sm ">"]
       [:input.text-sm.ikc-fg-text.ikc-bg-raised.border.ikc-bd-border.rounded.px-2.py-1.flex-1
        {:type        "text"
         :value       @buf
         :placeholder (if @sending "waiting for response..." "send a message to the agent...")
         :disabled    @sending
         :on-change   (fn [ev] (reset! err nil) (reset! buf (.. ev -target -value)))
         :on-key-down (partial handle-prompt-submit bus session-id buf sending err)}]
       (when @err
         [:span.text-xs.ikc-fg-warn.whitespace-nowrap {:data-prompt-refusal ""} @err])
       [:button.text-xs.ikc-fg-accent.hover:ikc-fg-accent-2.border.ikc-bd-border.rounded.px-2.py-1.whitespace-nowrap
        {:on-click #(save-conversation! bus session-id)}
        "save as note"]
       [:button.text-xs.ikc-fg-good.hover:ikc-fg-good.border.ikc-bd-border.rounded.px-2.py-1.whitespace-nowrap
        {:on-click #(open-file! bus)}
        "open file"]
       [:button.text-xs.ikc-fg-muted.hover:ikc-fg-text.border.ikc-bd-border.rounded.px-2.py-1.whitespace-nowrap
        {:on-click #(close-file! bus)}
        "close file"]])))

;; ── ikc1 top-bar chrome (host-level, PLAN-graph-ui-v0-006-01 Phase C) ─────────
;; The frame around every island — not island content, so it lives in the app
;; shell, not a DSL composition (the browser-chrome-around-a-page split). Colour
;; flows through the theme tokens (ADR-graph-ui-v0-009). Features not built yet are shown
;; grayed (the locked acceptance spec's "toggles grayed" decision).

(defn- tb-svg [& children]
  (into [:svg {:width 16 :height 16 :viewBox "0 0 24 24" :fill "none"
               :stroke "currentColor" :stroke-width 2}] children))

(defn- tbi
  "One top-bar icon button. :disabled? grays it (feature not built); :active?
  marks the current view. :import-id, when given, is the data-import-id a probe
  finds the button by."
  [{:keys [title active? disabled? on-click import-id]} icon]
  [:span {:title title
          :data-import-id import-id
          :on-click (when-not disabled? on-click)
          ;; ikc-touch-target: on a finger this span becomes at least
          ;; --touch-target across and tall (css/ikc-base.css). It is a SPAN
          ;; with an :on-click, so no element selector can reach it — the class
          ;; is the one hand-named case and it names what the element is.
          :class (str "ikc-touch-target inline-flex items-center justify-center w-7 h-7 rounded-md "
                      (cond disabled? "ikc-disabled cursor-default"
                            active?   "text-[var(--accent)]"
                            :else     "cursor-pointer text-[var(--muted)] hover:bg-[var(--hover)] hover:text-[var(--text)]"))
          :style (when active? {:background "var(--accent-soft)"})}
   icon])

(defn- mission-control
  "An icon that opens Mission Control (George, 2026-08-14: *\"Can we have icon to
  open mission control from the common top bar above the islands?\"*).

  IT OPENS THE HOST SERVING THIS PAGE, not localhost. Mission Control runs
  beside the bus, and this bundle is served BY the bus, so the host in the
  address bar is already the right one — hardcoding localhost sent a LAN browser
  to its own machine, where nothing is listening.

  NOT GREYED OFF LOOPBACK ANY MORE (George, 2026-08-14: *\"Make the Mission
  Control available on the LAN, I do not have to go to the VM to open it as
  localhost\"*). Greying was right while the dashboard bound 127.0.0.1 by
  decision; it now takes IKC_DASH_BIND, so whether it answers is a per-host
  choice this browser cannot see. A button that reports a fact it does not have
  is worse than a link that fails visibly: if the host has not widened its bind,
  the new tab shows an ordinary connection error, which is legible and
  fixable — a permanently grey icon is neither."
  []
  (let [host (.. js/window -location -hostname)
        url  (str "http://" host ":4666/")]
    [tbi {:title (str "Mission Control — services, logs, dev actions (" url
                      "). Needs IKC_DASH_BIND set on that host to answer from"
                      " anywhere but its own console.")
          :on-click (fn [_] (.open js/window url "_blank" "noopener"))}
     (tb-svg [:path {:d "M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"}]
             [:path {:d "M13.4 10.6 19 5"}]
             [:path {:d "M20.5 16a9 9 0 1 0-17 0"}])]))

(defn- tb-group [& children]
  (into [:div.flex.gap-0.5.px-1.5.items-center
         {:class "border-l" :style {:border-color "var(--border)"}}]
        children))

(defn- session-control
  "Current session + selector, in the top bar beside the connection (George,
  2026-07-19). The tab's own session is always the selected option; picking
  another JOINS it (this tab becomes a second live window onto the same shared
  session); '＋ new session' mints one.

  Archived sessions appear ONLY after the person picks 'show archived'
  (PLAN-ikc-v3-047-01): archiving a session takes it out of this list. Then
  they appear in their own group, and picking one RESTORES it — attaching to an
  archived session is what un-archives it, so restore needs no separate
  gesture. 'hide archived' takes the group away again.

  The archive control is deliberately SEPARATE from the dropdown: selecting and
  putting away are different kinds of act. Nothing here destroys a session — it
  keeps its history and conversation either way (George, 2026-07-19).

  Options are spread positionally via `into` — a nested bare vector in one child
  slot is the Reagent 'Key must be integer' trap (CLAUDE.md)."
  []
  (let [{:keys [session-id sessions show-archived?]} @app-state
        short    (fn [s] (str (subs (str s) 0 8) "…"))
        ;; The CURRENT session must use the same server-built label as every other
        ;; option. Showing a bare id for the current one and rich labels for the
        ;; rest reads as the selector not knowing what it is showing — George saw
        ;; exactly that live (2026-07-19). Fall back to the short id only before
        ;; the first :ui/list-sessions reply has arrived.
        {:keys [self open archived]} (picker-groups sessions session-id show-archived?)
        self-lbl (or (:label self) (short session-id))
        optgroup (fn [k lbl items]
                   (when (seq items)
                     (into [:optgroup {:key k :label lbl}]
                           (for [{:keys [value label]} items]
                             ^{:key value} [:option {:value value} label]))))]
    (when session-id
      [:div.flex.items-center.gap-1.text-xs.ml-2 {:class "text-[var(--muted)]"}
       ;; ── WHO IS ON THE OTHER SIDE (2026-09-13) ─────────────────────────
       ;;
       ;; George: add the who am I indication in the top bar. Until the sign-in
       ;; page landed the same day, the answer was always `nobody` — no tab ever
       ;; held a cookie, so no connection carried a person and nothing anyone
       ;; wrote recorded an author.
       ;;
       ;; A LINK, because the only thing to DO about it is sign in, and the page
       ;; that does that is not part of the bundle.
       (when-let [who (who-label (:whoami @app-state))]
         [:a {:href  (str "/sign-in.html?next=" (js/encodeURIComponent (.. js/window -location -pathname)
                                                                      ))
              :title "Who this connection is signed in as. The bus records the author of a thought only when it knows."
              ;; nowrap, or `not signed in` breaks into three lines and the
              ;; top bar grows (measured 2026-09-13).
              :class (str "px-1 rounded hover:underline whitespace-nowrap "
                          (if (:signed-in? (:whoami @app-state))
                            "text-[var(--text)]" "text-[var(--faint)]"))}
          who])
       ;; THE WORD `session` WENT ON 2026-09-13 (George: remove the label
       ;; `session`, it is already said in the names in the dropdown). Every
       ;; option reads `session 389a4a3c · research-canvas`, so the label
       ;; restated the first word of every line under it.
       (into [:select.rounded.border.bg-transparent.text-xs.px-1
              {:style     {:border-color "var(--border)" :color "var(--text)"}
               :title     "The session this tab renders — pick another surface's to see the same live state, 'show older' to list sessions older than 7 days, or 'show archived' to restore an archived one"
               :value     (str session-id)
               :on-focus  (fn [_] (refresh-sessions!))
               :on-change (fn [e]
                            (let [v (.. e -target -value)]
                              (cond
                                (= v "__new__")           (switch-session! nil)
                                ;; A LIST CHOICE, NOT A SESSION. The select is
                                ;; controlled by :value, so it snaps back to
                                ;; this tab's own session on the next render.
                                (= v "__show-archived__") (do (swap! app-state assoc :show-archived? true)
                                                              (refresh-sessions!))
                                (= v "__hide-archived__") (do (swap! app-state assoc :show-archived? false)
                                                              (refresh-sessions!))
                                ;; THE RETENTION RULE HIDES, AND THIS SHOWS
                                ;; (PLAN-ikc-v3-050-01). Nothing was archived.
                                (= v "__show-older__")    (do (swap! app-state assoc :show-older? true)
                                                              (refresh-sessions!))
                                (= v "__hide-older__")    (do (swap! app-state assoc :show-older? false)
                                                              (refresh-sessions!))
                                (not= v (str session-id)) (switch-session! (uuid v)))))}
              ^{:key "cur"} [:option {:value (str session-id)} self-lbl]]
             (remove nil?
                     [(optgroup "open" "open" open)
                      (optgroup "arch" "archived — pick to restore" archived)
                      ^{:key "new"} [:option {:value "__new__"} "＋ new session"]
                      (if (:show-older? @app-state)
                        ^{:key "hide-older"} [:option {:value "__hide-older__"} "hide older"]
                        ^{:key "show-older"} [:option {:value "__show-older__"} "show older"])
                      (if show-archived?
                        ^{:key "hide"} [:option {:value "__hide-archived__"} "hide archived"]
                        ^{:key "show"} [:option {:value "__show-archived__"} "show archived"])]))
       [:span.cursor-pointer.px-1 {:class "text-[var(--faint)] hover:text-[var(--warn)]"
                                   :title "Archive this session — every surface detaches and it leaves the list, but it keeps its history and conversation. 'show archived' in the list restores it"
                                   :on-click (fn [_] (archive-session!))}
        "⌸"]])))

;; ---------------------------------------------------------------------------
;; "Your click landed, and it is going to take a moment" (2026-08-17).
;;
;; These live in the CHROME, not in an island, and that placement is the design
;; decision rather than a convenience. An island is a thing ON the canvas; "the
;; client is waiting for the bus" is a fact about the WINDOW you are looking
;; through, true of every island at once. Putting a viewport-spanning progress
;; bar inside an island description would make the canvas answer for the frame
;; (George, on tokens: a pixel is not canvas-native — the same boundary).
;;
;; SEPARATE COMPONENTS on purpose. graph-ui.web.busy ticks every 100ms while
;; anything is outstanding, and whatever derefs it re-renders at that rate. Two
;; small components re-render two spans; folding them into top-bar would repaint
;; the whole chrome — brand mark, every toolbar button, the session <select> — ten
;; times a second for the duration of every wait.
;; ---------------------------------------------------------------------------

(defn- busy-bar
  "An indeterminate sweep along the chrome bar's bottom edge while the client is
   waiting on the bus. Indeterminate because the length genuinely is not known in
   advance: one click is one dispatch plus one :ui/read per source the server says
   changed, so a percentage would be a number we invented."
  []
  (let [{:keys [phase]} (busy/state)]
    (when (contains? #{:busy :slow} phase)
      [:div.ikc-busy-bar {:data-phase (name phase) :aria-hidden true}])))

(defn- busy-readout
  "The indicator beside the connection LED — IN A SLOT THAT IS ALWAYS THERE.

   George, 2026-08-17: *\"I saw the spinning ring in the top bar, but it should
   live in a fixed space, the realignment of the elements after it is disturbing
   and breaks 'Do not make me think' principle.\"* Exactly right, and the miss is
   worse than an oversight: the ROW's hourglass was deliberately put at the
   trailing edge so that appearing would not move the label under the cursor that
   had just clicked it — and then the same care was not taken one component over.
   This was an ordinary inline flex child, so every appearance pushed the session
   dropdown sideways and every disappearance pulled it back. An indicator that
   moves the furniture costs more attention than the wait it reports.

   So the slot is rendered ALWAYS, at a fixed width, and only its CONTENTS change.
   Nothing outside it can move, in any phase, ever.

   THE STRINGS ARE COMPACT BECAUSE THE SLOT IS RESERVED. Reserved width is spent
   whether or not it is used, so 'still working · 12s' would mean ~20ch of
   permanently empty chrome. The prose moved to the tooltip and the slot keeps what
   cannot be said any other way:

     one request, waiting  -> the ring alone. The ring IS the message.
     several              -> ×3, because how MANY is the only extra fact.
     past slow-after-ms   -> 12s, and the ring turns --warn with the bar.

   Nothing is lost: the full sentence is the `title`, and the stall still announces
   itself in colour on two surfaces at once."
  []
  (let [{:keys [phase waited label] n :count} (busy/state)
        busy?  (contains? #{:busy :slow} phase)
        slow?  (= phase :slow)]
    [:span.ikc-busy-slot
     {:title      (or label "idle — nothing waiting on the bus")
      :aria-label (when busy? (or label "waiting for the bus"))}
     (when busy?
       [:<>
        [:span.ikc-busy-spinner {:aria-hidden true
                                 :data-phase (name phase)}]
        ;; role=status on the DIGITS only, and only while slow: an aria-live region
        ;; that re-announced "waiting" on every routine read would talk over the
        ;; reader ten times a second for no information.
        (cond
          slow?    [:span {:role "status" :style {:color "var(--warn)"}}
                    (str (.toFixed (/ waited 1000) 0) "s")]
          (> n 1)  [:span.ikc-dim {:aria-hidden true} (str "×" n)]
          :else    nil)])]))

(defn- island-picker
  "The island's own NAME in the bar, turned into the way to change it.

  IT ADDS NO WIDTH, which is why it is here rather than beside the view
  toggles. The bar already showed the attached island's title next to the
  brand, purely as a label; the thing that says WHICH island you are on is the
  natural thing to press to go to another. A sixteenth control in this row
  would have cost the connection address on a phone.

  IT LOOKS LIKE THE DROPDOWN IT IS (George, 2026-09-02: *\"before open it is
  subtle colored, without traditional dropdown UI - no border, it is not
  serious to have the triangle from three pixels\"*). The first version was
  faint text and a 0.7em unicode triangle, which read as a label. It now wears
  the SAME clothes as the session <select> three controls to its right —
  `rounded`, `border`, `border-color var(--border)`, the island title in
  `var(--text)` — because two dropdowns in one bar that look unrelated are two
  bugs. The caret is a real 16px chevron from tb-svg, the same drawing every
  other icon in this bar uses, and it turns when the menu is open.

  THE MENU IS position:fixed, AND THAT IS A FIX RATHER THAN A STYLE. The bar
  carries `overflow-x: hidden` so a phone cannot drag the chrome sideways. CSS
  computes the OTHER axis of an overflow pair to `auto`, so the bar is a scroll
  container 40px tall, and an absolutely-positioned menu inside it was clipped
  to that: George saw only its top border (2026-09-02). A fixed element is
  positioned against the viewport and no ancestor's overflow clips it — the
  same reason xray/overlay is fixed. Its place is measured from the trigger on
  open, because a fixed box cannot inherit one.

  EVERY ROW IS A REAL ANCHOR, and that is what gives the two ways for free.
  A click loads the island in this tab, because that is what an href does.
  Ctrl/Cmd+click, middle-click, and the browser's own context menu all open it
  in a NEW tab — behaviour no code here implements and none can get wrong. The
  trailing icon is the same destination with target=_blank, so the second way
  is discoverable without knowing a modifier (CONCEPT-graph-ui-v0-010-01: an
  affordance a reader cannot see is not one).

  THE CURRENT ISLAND IS A NOOP ON A PLAIN CLICK (George, 2026-09-02). A reload
  to arrive where you already are costs a second and loses the session's place.
  Its NEW-TAB route stays live, because a second window onto one island is a
  thing to want, so the suppression checks for a modifier key before it fires.

  Dismissal is a full-viewport transparent backdrop BEHIND the menu, not a
  document listener: it needs no mount/unmount bookkeeping and cannot outlive
  the component that opened it."
  []
  (let [open? (r/atom false)
        at    (r/atom {:top 0 :left 0})]
    (fn []
      (let [{:keys [bus]} @app-state
            current (island-from-url)
            rows    (islands/entries)
            close!  #(reset! open? false)]
        [:span {:style {:display "inline-flex"}}
         [:button
          {:title    "Choose an island"
           :aria-haspopup "menu"
           :aria-expanded (str (boolean @open?))
           :on-click (fn [e]
                       ;; Measure BEFORE the state flips: the menu is fixed, so
                       ;; it needs the trigger's viewport box, and after a
                       ;; re-render this event's target is gone.
                       (let [r (.getBoundingClientRect (.-currentTarget e))]
                         (reset! at {:top (+ (.-bottom r) 4) :left (.-left r)}))
                       ;; Read on OPEN, not on mount: the bar is built before
                       ;; the socket is up, and a list read at boot comes back
                       ;; empty and stays empty until something re-renders it.
                       (when-not @open? (islands/refresh! bus))
                       (swap! open? not))
           :class "ikc-touch-target inline-flex items-center gap-1 rounded border
                   px-1.5 py-0.5 text-xs font-normal cursor-pointer
                   hover:bg-[var(--hover)]"
           :style {:border-color (if @open? "var(--accent)" "var(--border)")
                   :color        "var(--text)"
                   :background   "transparent"}}
          current
          [:span {:style {:display "inline-flex"
                          :transform (when @open? "rotate(180deg)")
                          :transition "transform 120ms"
                          :color "var(--muted)"}}
           (tb-svg [:path {:d "m6 9 6 6 6-6"}])]]
         (when @open?
           [:<>
            ;; The backdrop. Behind the menu, over everything else.
            ;;
            ;; THE TWO LAYERS ARE NAMED, NOT NUMBERED (2026-09-07). They were a
            ;; bare 60 and 61 — the only raw z-index left in this renderer,
            ;; while every other popup in it already reads a token
            ;; (widgets.cljs' popover and the theme editor both take
            ;; `--layer-modal`). dsl/instances/themes.edn declares the four
            ;; ordered steps for exactly this: "which is in front is a decision
            ;; with names instead of a number someone picked". A backdrop is an
            ;; overlay and a menu over it is modal, which is what those two
            ;; names mean, and the ladder keeps them one step apart on its own.
            [:div {:on-click close!
                   :style {:position "fixed" :inset 0 :zIndex "var(--layer-overlay)"}}]
            [:div {:role "menu"
                   :style {:position "fixed"
                           :top (str (:top @at) "px") :left (str (:left @at) "px")
                           :zIndex "var(--layer-modal)"
                           :minWidth "18ch" :maxHeight "60vh" :overflowY "auto"
                           :background "var(--panel)" :border "1px solid var(--border)"
                           :borderRadius "6px" :padding "4px"
                           :boxShadow "0 8px 24px rgba(0,0,0,0.35)"}}
             (if (empty? rows)
               [:div {:style {:padding "6px 8px" :color "var(--faint)" :fontSize "12px"}}
                "reading the islands\u2026"]
               (into [:<>]
                     (map (fn [{:keys [label value]}]
                            (let [here? (islands/current? label current)
                                  url   (islands/href label)]
                              ^{:key (str value)}
                              [:div {:style {:display "flex" :alignItems "center" :gap "2px"}}
                               [:a {:href  url
                                    :title (if here? "Already open in this tab" (str "Open " label " in this tab"))
                                    :on-click (fn [e]
                                                ;; A modifier means the browser is
                                                ;; about to open a new tab. Leave it
                                                ;; alone even on the current island.
                                                (if (and here?
                                                         (not (.-ctrlKey e)) (not (.-metaKey e))
                                                         (not (.-shiftKey e)) (= 0 (.-button e)))
                                                  (do (.preventDefault e) (close!))
                                                  (close!)))
                                    :class "hover:bg-[var(--hover)]"
                                    :style (cond-> {:flex 1 :padding "5px 8px" :borderRadius "4px"
                                                    :fontSize "13px" :textDecoration "none"
                                                    :whiteSpace "nowrap"
                                                    :color (if here? "var(--accent)" "var(--text)")}
                                             here? (assoc :fontWeight 600))}
                                (when here? [:span {:style {:marginRight "5px"}} "\u2022"])
                                label]
                               ;; ALWAYS DRAWN, never hover-revealed: a control
                               ;; that appears on hover moves the row it is in,
                               ;; and this one has a reserved slot instead.
                               [:a {:href url :target "_blank" :rel "noopener"
                                    :title (str "Open " label " in a NEW tab")
                                    :on-click #(close!)
                                    :class "ikc-touch-target hover:bg-[var(--hover)]"
                                    :style {:display "inline-flex" :alignItems "center"
                                            :padding "4px" :borderRadius "4px"
                                            :color "var(--faint)" :textDecoration "none"}}
                                    (tb-svg [:path {:d "M14 3h7v7"}]
                                            [:path {:d "M21 3l-9 9"}]
                                            [:path {:d "M19 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5"}])]]))
                          rows)))
             ;; REPORT A FAULT, IN THE ISLAND MENU AND NOT ON THE SIDEBAR ROW
             ;; (PLAN-ikc-v3-039-01 §2). George has found that row too crowded.
             ;; Every island has this menu, so every island can report, and
             ;; the row costs the bar no width.
             [:div {:style {:borderTop "1px solid var(--border)" :margin "4px 0"}}]
             [:button {:data-import-id "report-a-fault"
                       :title "File a fault report. The page, the error and your last steps are collected for you."
                       :on-click (fn [_]
                                   (close!)
                                   (support/report-fault!
                                     {:bus        bus
                                      :session-id (:session-id @app-state)
                                      :island     current}))
                       :class "hover:bg-[var(--hover)]"
                       :style {:display "block" :width "100%" :textAlign "left"
                               :padding "5px 8px" :borderRadius "4px" :fontSize "13px"
                               :background "transparent" :border "none" :cursor "pointer"
                               :color "var(--text)"}}
              "Report a fault"]]])]))))

(defn top-bar-box
  "The attributes of the top bar's own box: one row that scrolls sideways
  inside itself, on every width (fault report 49fa502b). A function of its
  own so a test can hold the rule with no browser."
  []
  ;; THE BAR IS ONE ROW THAT SCROLLS SIDEWAYS, ON EVERY WIDTH (bug 3 of
  ;; IKC3-Bugs-20260928, then fault report 49fa502b of 2026-10-05). It used
  ;; to wrap. At 44-pixel touch targets that was three or four rows of chrome
  ;; above the page. One row keeps the reading pane its height, and a thumb
  ;; reaches every control. The PAGE never moves sideways: only this bar
  ;; scrolls, inside its own box, which is what :narrow :scroll means for a
  ;; node.
  ;;
  ;; THE SCROLL IS NOT KEYED TO THE WIDTH. Until 2026-10-05 only a window
  ;; below --viewport-narrow got it, and a wider window kept
  ;; `overflow-x: hidden`. But a finger also sets the width of this row: it
  ;; makes each control 44 px (ikc-base.css, Rule 2). A Boox reader in
  ;; landscape is 856 px wide and its row was 1,978 px, so 1,122 px of
  ;; controls were cut off with no way to reach them. A tablet and a phone
  ;; in landscape had the same fault. Where the row fits, the scroll box
  ;; changes nothing. The narrow answer now says only WHICH controls show.
  {:class "min-h-10 flex-nowrap whitespace-nowrap [&>*]:flex-shrink-0 border-b"
   :data-import-id "top-bar"
   :style {:background "var(--panel)" :border-color "var(--border)"
           ;; The containing block for .ikc-busy-bar,
           ;; which rides the bar's own bottom edge.
           :position "relative"
           :max-width "100%"
           :overflow-x "auto" :overflow-y "hidden"
           :overscroll-behavior-x "contain"
           :scrollbar-width "thin"}})

(defn top-bar
  "The ikc1 chrome bar: nav, brand, connection + status, view/sync toggles
  (grayed until built), and the theme controls (config opens the editor).

  ON A PHONE IT DROPS WHAT DOES NOTHING (PLAN-ikc-v3-015-01 phase 6). This bar
  is fifteen controls, a brand mark, a websocket address and a session selector
  in ONE non-wrapping row, so on a 360px window it ran off the right edge and
  took the whole page's horizontal scroll with it. Below --viewport-narrow it
  keeps the controls that DO something and hides the ones drawn grey — a button
  that is grey because the feature is not built has nothing to offer a phone,
  and hiding it costs the reader nothing that exists.

  It is CODE and not a widget tree on purpose, which is this bar's own standing
  decision (see the comment above tbi): the chrome is the frame around every
  island, not island content, so ADR-graph-ui-v0-011-01's model-declared sizing
  governs the islands and this row governs itself. The number is still declared
  — graph-ui.web.responsive reads it from the theme."
  []
  (let [{:keys [status bus session-id]} @app-state
        narrow? (responsive/narrow?)
        led  (case status
               :attached "var(--good)"
               (:connecting :connected) "var(--warn)"
               "var(--crit)")
        conn (origin/without-scheme (origin/page-bus-url))]
    [:div.flex.items-center.gap-1.px-2.flex-shrink-0
     (top-bar-box)
     [busy-bar]
     ;; BUILT SINCE 2026-09-01, and therefore NO LONGER INSIDE the narrow guard
     ;; (F-graph-ui-v0-013-01). The rule that hid these two was "a button drawn
     ;; grey has nothing to offer a phone", which was right while they were inert
     ;; and is wrong now: going back is worth MORE on a phone, where the sidebar
     ;; is folded and a wikilink is the only way in. Two 28px targets, and the
     ;; row already wraps at this width.
     ;;
     ;; They still grey themselves, from graph-ui.web.nav rather than from a
     ;; literal — an empty trail has nowhere to go, which is the honest reading
     ;; of CONCEPT-graph-ui-v0-010-01 rather than a promise that never resolves.
     [tbi {:title     (if (nav/can-back?) "Back" "Back — no page visited yet")
           :disabled? (not (nav/can-back?))
           :on-click  #(nav/back! bus session-id)}
      (tb-svg [:path {:d "m15 18-6-6 6-6"}])]
     [tbi {:title     (if (nav/can-forward?) "Forward" "Forward — nothing to return to")
           :disabled? (not (nav/can-forward?))
           :on-click  #(nav/forward! bus session-id)}
      (tb-svg [:path {:d "m9 18 6-6-6-6"}])]
     ;; THE PALETTE'S BUTTON (SPEC-alatus-v0-002-02 row C-80). Ctrl+K asks for
     ;; it on a keyboard; a phone has no Ctrl+K, and an affordance nobody can
     ;; see is not one — the button is drawn on every width, greyed only on an
     ;; island with no reading pane to search into (CONCEPT-graph-ui-v0-010-01).
     [tbi {:title     "Search pages — opens the command palette (Ctrl+K)"
           :disabled? (not (:page-addressable? @app-state))
           :on-click  #(palette-set-open! true)}
      (tb-svg [:circle {:cx 11 :cy 11 :r 7}]
              [:path {:d "m21 21-4.3-4.3"}])]
     ;; REPORT WHAT YOU FOUND, FROM EVERY ISLAND, IN ONE CLICK
     ;; (IKC3-Bugs-20261003: "Need Mic/Voice/Support icon on the common top
     ;; bar"). The same flow as the last row of the island menu, which stays.
     ;; Beside Search, so a thumb reaches it before the bar scrolls.
     ;;
     ;; A SPEECH BUBBLE, NOT A MICROPHONE. The flow asks for typed words, and a
     ;; microphone would promise a recording that is not built
     ;; (ADR-ikc-v3-082-01 is proposed). On a phone or DeX, the keyboard's own
     ;; microphone fills the field.
     [tbi {:title     "Report what you found. The page, the error and your last steps are collected for you."
           :import-id "top-bar/report-a-fault"
           :disabled? (nil? bus)
           :on-click  #(support/report-fault! {:bus        bus
                                               :session-id session-id
                                               :island     (island-from-url)})}
      (tb-svg [:path {:d "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"}]
              [:path {:d "M12 7v4"}]
              [:path {:d "M12 14h.01"}])]
     ;; THE MARK — two islands, the far one running off its own edge, because the
     ;; canvas does not end. dsl/design/brand.edn declares it; the drawing is
     ;; resources/public/brand/ikc-mark.svg and this is the same shape inline, so
     ;; the top bar needs no network request for four rectangles.
     ;;
     ;; Sized and spaced from the brand tokens rather than from numbers typed
     ;; here: --brand-min-size is the size below which the two islands stop
     ;; reading as two, and --brand-clear-space is a RATIO of the mark's height,
     ;; which is how a mark states clear space and the only form that survives
     ;; zoom on a canvas.
     [:svg {:view-box "0 0 24 24" :role "img" :aria-label "IKC"
            :style {:width "var(--brand-min-size)" :height "var(--brand-min-size)"
                    :marginInline "calc(var(--brand-min-size) * var(--brand-clear-space))"
                    :flexShrink 0}}
      [:rect {:x 2.5 :y 2.5 :width 12 :height 12 :rx 3 :fill "var(--accent)"}]
      [:rect {:x 9 :y 9 :width 14.5 :height 14.5 :rx 3
              :fill "none" :stroke "currentColor" :stroke-width 1.75}]]
     [:div.flex.items-baseline.gap-1.5.font-bold {:class "text-[var(--heading)]"}
      "ikc3"
      ;; UI build stamp (ADR-graph-ui-v0-010-01): a legible id of the running bundle,
      ;; so 'is my code even live?' is answerable at a glance, before attach.
      [:span.text-xs.font-normal.font-mono {:class "text-[var(--faint)]" :title "UI build id"}
       (str "· " version/build-id)]]
     [:div.flex.items-center.gap-1.5.text-xs.ml-1 {:class "text-[var(--muted)]"}
      [:span {:style {:width "7px" :height "7px" :border-radius "50%" :background led}}]
      ;; The LED says whether the bus is reachable. The ADDRESS says which bus,
      ;; and on a phone it is 30 characters the reader cannot act on — the LED
      ;; and the build id carry the same two facts in a tenth of the width.
      (when-not narrow? conn)
      ;; Beside the LED because it answers the neighbouring question. The LED says
      ;; whether the bus is REACHABLE; this says whether it is currently BUSY, and
      ;; before today a reader could only tell the two apart by waiting.
      [busy-readout]]
     ;; ORDER IS GEORGE'S, 2026-09-02: arrows, mark, wordmark, build id, then the
     ;; LED and its address, THEN the island picker, THEN the session picker.
     ;;
     ;; The two pickers now sit together, which is the reason it reads better.
     ;; They answer the same kind of question — WHICH one am I looking at — and
     ;; the island picker was previously inside the brand group, between the
     ;; wordmark and the build id, where it split one identity into two halves.
     ;; What is left of that group is the mark, the name and the build id: what
     ;; this SOFTWARE is. What follows is what this TAB is pointed at.
     [island-picker]
     [session-control]
     [:div.flex-1]
     ;; view toggles — Outline is the only built one
     [:div.flex.gap-0.5.px-1.5.items-center
      ;; THE SIDEBAR'S FOLD, beside its drag (2026-09-05). George: "it should
      ;; have collapse beside width control". ikc1 had both on that edge —
      ;; AppShell.tsx keeps isSidebarCollapsed with a toggleSidebar AND a drag,
      ;; and a drag un-collapses. reader-v2's :split-pane kept the pair as a
      ;; chevron; reader-v3's dock had the drag and had lost the fold.
      (let [id "navigator" have? (dock-panes/available? id)]
        [tbi {:title (if have?
                       (if (dock-panes/showing? id) "Sidebar — collapse" "Sidebar — show")
                       "Sidebar (not on this island)")
              :disabled? (not have?)
              :active?   (and have? (dock-panes/showing? id))
              :on-click  #(dock-panes/toggle! id)}
         (tb-svg [:rect {:x 3 :y 4 :width 18 :height 16 :rx 2}] [:path {:d "M9 4v16"}])])
      [tbi {:title "Outline view" :active? true} (tb-svg [:path {:d "M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"}])]
      ;; ONLY THE GREY ONE STAYS BEHIND THE NARROW GUARD. The Graph and
      ;; Internals toggles were inside it too, from before they were wired, so
      ;; a phone could open those panes and had no way to close them (bug 3 of
      ;; IKC3-Bugs-20260928).
      (when-not narrow?
      [tbi {:title "All pages (not built yet)" :disabled? true} (tb-svg [:rect {:x 3 :y 3 :width 7 :height 7 :rx 1}] [:rect {:x 14 :y 3 :width 7 :height 7 :rx 1}] [:rect {:x 3 :y 14 :width 7 :height 7 :rx 1}] [:rect {:x 14 :y 14 :width 7 :height 7 :rx 1}])])
      ;; THE TWO PANE TOGGLES, WIRED 2026-09-05. They said "not built yet" for a
      ;; day after the panes were built, which is the "declared is not wired"
      ;; smell George has caught here before.
      ;;
      ;; ikc1's RULE, COPIED (App.tsx toggleGraph / toggleInternals): two
      ;; independent booleans, both true by default, kept in the browser. Its
      ;; own button showed state as `variant={showGraph ? 'secondary' : 'ghost'}`
      ;; — which is what :active? is here.
      ;;
      ;; DISABLED WHEN THE ISLAND HAS NO SUCH PANE. ikc1 was one application and
      ;; never needed this. Only reader-v3 draws these two, so on any other
      ;; island the icon is greyed rather than pressed to no effect
      ;; (CONCEPT-graph-ui-v0-010-01).
      (let [id "graph-view" have? (dock-panes/available? id)]
        [tbi {:title (if have?
                       (if (dock-panes/showing? id) "Graph view — hide" "Graph view — show")
                       "Graph view (not on this island)")
              :disabled? (not have?)
              :active?   (and have? (dock-panes/showing? id))
              :on-click  #(dock-panes/toggle! id)}
         (tb-svg [:circle {:cx 5 :cy 6 :r 2}] [:circle {:cx 19 :cy 8 :r 2}] [:circle {:cx 12 :cy 18 :r 2}] [:path {:d "M7 7l10 1M17 10l-4 6M7 8l4 8"}])])
      ;; TWO NOTES SIDE BY SIDE, WIRED 2026-09-06. ikc1 had exactly one control
      ;; for this and it was here: a `Columns` button whose tooltip reads "Show
      ;; side-by-side note" or "Hide side-by-side note" (App.tsx
      ;; toggleSecondPage), off by default.
      ;;
      ;; IT SETS A TILE COUNT. A tile IS a second reading pane — its own session,
      ;; its own note (ADR-graph-ui-v0-023-01). The pane already drew itself once
      ;; per tile; nothing could open the second one.
      ;;
      (let [id "frame-internals" have? (dock-panes/available? id)]
        [tbi {:title (if have?
                       (if (dock-panes/showing? id)
                         "System internals — hide"
                         "System internals — show")
                       "System internals (not on this island)")
              :disabled? (not have?)
              :active?   (and have? (dock-panes/showing? id))
              :on-click  #(dock-panes/toggle! id)}
         (tb-svg [:path {:d "M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6z"}])])
      ;; THE DRAWER OF THE SUPERVISOR (IKC3/PLAN-ikc-v3-067-01/SV-2, screen 10
      ;; of PLAN-ikc-v3-080-01). George, D6: "The drawer". Each tracker island
      ;; holds the pane supervisor-pane, and this button opens and folds it.
      ;; On another island it is grey, as the three toggles before it.
      (let [id "supervisor-pane" have? (dock-panes/available? id)]
        [tbi {:title (if have?
                       (if (dock-panes/showing? id) "Supervisor — close the drawer" "Supervisor — open the drawer")
                       "Supervisor (not on this island)")
              :disabled? (not have?)
              :active?   (and have? (dock-panes/showing? id))
              :on-click  #(dock-panes/toggle! id)}
         (tb-svg [:path {:d "M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z"}]
                 [:path {:d "M9 11h.01M12 11h.01M15 11h.01"}])])]
     [tb-group
      (when-not narrow?
       [:<>
        [tbi {:title "Server connection (not built yet)" :disabled? true} (tb-svg [:rect {:x 3 :y 4 :width 18 :height 8 :rx 2}] [:rect {:x 3 :y 14 :width 18 :height 6 :rx 2}] [:path {:d "M7 8h.01M7 17h.01"}])]
        [tbi {:title "GitHub sync (not built yet)" :disabled? true} (tb-svg [:path {:d "M6 3v12M6 15a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM6 3a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM18 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM18 9a9 9 0 0 1-9 9"}])]
        [tbi {:title "Import / Export / Backup / Restore (not built yet)" :disabled? true} (tb-svg [:path {:d "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"}])]])
      ;; The ruler is an INSTRUMENT, so it sits with the other tools rather than
      ;; with the view toggles: it does not change what you are looking at, it
      ;; tells you how wide it is (George, 2026-08-14).
      [tbi {:title "Ruler — measure widths in ch (click the readout for px)"
            :active? (ruler/shown?)
            :on-click ruler/toggle!}
       (tb-svg [:rect {:x 2 :y 8 :width 20 :height 8 :rx 1}]
               [:path {:d "M7 8v3M12 8v4M17 8v3"}])]
      ;; X-ray sits beside the ruler for the same reason the ruler sits here: it
      ;; is an INSTRUMENT, not a view. The ruler says how wide the thing is, this
      ;; says what its parts are CALLED — the pair a sentence about a widget
      ;; needs (F-graph-ui-v0-010-01).
      ;;
      ;; Three states, so the tooltip carries the state it is IN and the one a
      ;; click moves to; `:active?` covers both on-states, since "ringed, names
      ;; on hover" is emphatically still on.
      [tbi {:title    (xray/label)
            :active?  (boolean (xray/mode))
            :on-click xray/toggle!}
       ;; Nested rectangles with a tag on the top edge — the thing it draws,
       ;; drawn. The dashed inner rect is the ring; the short bar on its top edge
       ;; is the name plate.
       (tb-svg [:rect {:x 3 :y 4 :width 18 :height 16 :rx 2}]
               [:rect {:x 7 :y 9 :width 10 :height 7 :rx 1 :stroke-dasharray "2 1.5"}]
               [:path {:d "M7 9h4"}])]
      [mission-control]]
     [:div.flex.items-center.gap-2.px-1.5 {:class "border-l" :style {:border-color "var(--border)"}}
      [tbi {:title "Theme colors config" :on-click theme/toggle-editor!}
       (tb-svg [:circle {:cx 13.5 :cy 6.5 :r 2}] [:circle {:cx 17 :cy 12 :r 2}] [:circle {:cx 8.5 :cy 7 :r 2}] [:circle {:cx 7 :cy 12.5 :r 2}] [:path {:d "M12 22a3 3 0 0 0 0-6 2 2 0 0 1 0-4 10 10 0 1 0-10-10"}])]
      [theme/theme-selector]
      (when (= status :attached)
        [:span.text-xs.underline.cursor-pointer {:class "text-[var(--crit)]"
                                                 :on-click #(detach!)} "detach"])]]))

(defn- stale-ui-banner
  "The visible half of the UI-version transparent contract (ADR-graph-ui-v0-010-01):
  when the server reports a different build id than this browser is running, say
  so — loudly, with the fix — instead of letting stale code masquerade as a bug.
  The server also emits a :bus/intervention for the observability surface.

  THE FIX IS A CONTROL, NOT A KEY (fault report 0cb9ddff,
  IKC3/PLAN-ikc-v3-060-01/FR-T). A tablet or a phone has no hard reload, so the
  banner draws a button that loads the new bundle — graph-ui.web.stale."
  []
  (stale/banner (:stale-ui @app-state) stale/load-new-bundle!))

(defn- resumed-banner
  "George's three-way idle choice (2026-07-19), shown when this surface RESUMED
  an idle session on open rather than minting a fresh one.

  The session is already adopted — 'opened by default' was the decision — so the
  choice is about what to do with that fact: keep it (dismiss), start a new one
  instead (the idle session stays idle and rejoinable), or archive it. Stating
  which session was resumed, and how long it had been idle, is the point: a
  silent adoption would be indistinguishable from a bug the first time a tab
  came back with someone else's state in it."
  []
  (when-let [r (:resumed @app-state)]
    (let [btn (fn [label title on-click]
                [:button {:class "px-2 py-0.5 rounded border text-xs"
                          :style {:border-color "currentColor"}
                          :title title
                          :on-click on-click}
                 label])]
      [:div {:class "px-3 py-2 text-sm flex items-center gap-2 flex-shrink-0 flex-wrap"
             :style {:background "var(--accent-soft)" :color "var(--text)"}}
       [:span.font-semibold "↩ Resumed session:"]
       [:span (:label r)]
       [:span.ikc-strong "— it was idle on this island, so this tab picked it up."]
       [:div.flex.gap-1.ml-auto
        (btn "Keep it" "Carry on in this session"
             (fn [_] (swap! app-state dissoc :resumed)))
        (btn "Start new instead" "Leave this session idle and rejoinable; open a fresh one"
             (fn [_] (switch-session! nil)))
        (btn "Archive it" "Put this session away — restorable later from the selector"
             (fn [_] (swap! app-state dissoc :resumed) (archive-session! false)))]])))

;; ---------------------------------------------------------------------------
;; The guide's coach mark — application chrome, like the top bar
;; ---------------------------------------------------------------------------
;;
;; C-20, lane RK of PLAN-alatus-v0-003-01. A coach mark is declared chrome, not
;; island content (dsl/platform/guide-coach.edn): research mounts one inside
;; its own DSL block, but reader-v3 shares its page with reader-v2 and the
;; page/pattern validator wants pane-for-part — no file it owns can carry the
;; node. So the shell draws it, fed by the same :guide-current the declared
;; mount binds, and only under the conditions a DSL mount would imply.
;;
;; WHEN IT DRAWS. The island names a guide (:guide-count > 0), the tree the
;; island drew carries no :coach-mark of its own — research's does, and two
;; marks over one guide would ring each other's target — and the guide is not
;; walked.

(defn- guide-chrome-mount!
  "Subscribe + read the two sources the overlay draws — the same act
   subscribe-new-sources! does for bound pairs, for two that live outside the
   tree. Idempotent: :subscribed-sources and the snapshot guard each half."
  []
  (let [{:keys [bus session-id subscribed-sources sources]} @app-state]
    (when (and bus session-id)
      (doseq [[source args :as key] [[:guide-current {:session-id session-id}]
                                     [:guide-count   {:session-id session-id}]]]
        (when-not (contains? subscribed-sources key)
          (swap! app-state update :subscribed-sources conj key)
          (js-invoke bus "dispatch" (cond-> {:type :ui/subscribe :source source}
                                      (seq args) (assoc :args args))
            (fn [evt]
              (when-let [src (:source evt)]
                (on-source-changed bus {:source src :args args})))))
        (when-not (contains? sources (bind/snapshot-key source args))
          (queue-read! bus source args))))))

(defn- coach-overlay
  "The guide's coach mark, drawn by the shell for an island whose own tree
   declares none — the same widget, sources and gestures as the DSL mount.
   Reads app-state itself, so the render tree stays untouched."
  []
  (r/create-class
   {:component-did-mount  (fn [_] (guide-chrome-mount!))
    :component-did-update (fn [_ _] (guide-chrome-mount!))
    :reagent-render
    (fn []
      (let [{:keys [bus session-id render-tree sources]} @app-state
            args     {:session-id session-id}
            cur      (get sources (bind/snapshot-key :guide-current args))
            n        (long (or (get sources (bind/snapshot-key :guide-count args)) 0))
            ;; A DSL mount announces itself as a :coach-mark node in the tree.
            mounted? (some #(= :coach-mark (:type %))
                           (tree-seq map? #(filter map? (:children %)) render-tree))]
        ;; :walked? AND :hidden? CLOSE IT TOO (2026-09-30). A pinned step kept
        ;; :done-all? false after the work was done, and the card had no close.
        (when (and bus (pos? n) (not mounted?) (map? cur)
                   (not (:done-all? cur)) (not (:walked? cur)) (not (:hidden? cur)))
          ;; fixed, bottom-right: the card floats over whatever pane is under
          ;; it, and the widget still rings its :where target when one draws.
          [:div {:class "ikc-guide-chrome"
                 :style {:position "fixed" :right "1rem" :bottom "1rem"
                         :zIndex "var(--layer-overlay)"}}
           [widgets/widget-coach-mark
            {:target    (:where cur)
             :step      (:id cur)
             :title     (:title cur)
             :says      (:says cur)
             :index     (:index cur)
             :count     (:count cur)
             :has-do?   (:has-do? cur)
             :done?     (:done? cur)
             :pinned?   (:pinned? cur)
             :done-all? (:done-all? cur)
             ;; The same five gestures research-guide.edn declares, verbatim —
             ;; chrome changes WHERE the card sits, never what it sends.
             :on-gesture {:submit   {:command :ui/guide-do-for-me :with {:session-id '$session-id}}
                          :previous {:command :ui/guide-show :with {:session-id '$session-id :step '$value}}
                          :next     {:command :ui/guide-show :with {:session-id '$session-id :step '$value}}
                          :resume   {:command :ui/guide-resume :with {:session-id '$session-id}}
                          :restart  {:command :ui/close-inquiry :with {:session-id '$session-id}}
                          ;; The sixth gesture, chrome only: the declared mount
                          ;; sits in a pane the person can close already.
                          :close    {:command :ui/guide-hide :with {:session-id '$session-id}}}}
            bus session-id]])))}))

(defn app
  "The root reagent component. Renders the top bar + the live render tree
  (when attached) or a fallback message, plus the theme-editor drawer."
  []
  (let [{:keys [status render-tree error bus session-id]} @app-state]
    ;; 100dvh AFTER h-screen's 100vh (bug 3 of IKC3-Bugs-20260928). On Firefox
    ;; and Chrome for Android, 100vh is the height with the browser's own bar
    ;; HIDDEN, so while that bar shows, the bottom of the island is under it.
    ;; dvh follows the bar. A browser that does not know dvh drops the inline
    ;; value and keeps the class.
    [:div.h-screen.w-screen.flex.flex-col {:style {:background "var(--ground)"
                                                   :height "100dvh"}}
     [top-bar]
     ;; DIRECTLY UNDER THE BAR AND ABOVE EVERYTHING ELSE, because a ruler that
     ;; does not start at the same left edge as the panes below it measures
     ;; nothing: the sidebar is 34ch, so its edge has to land on the 34ch tick.
     [ruler/ruler]
     [stale-ui-banner]
     [resumed-banner]
     [theme/editor]
     ;; X-ray's rings and names (F-graph-ui-v0-010-01). Mounted ONCE here, as a
     ;; sibling of everything rather than a wrapper around anything: it is
     ;; position:fixed and pointer-events:none, so where it sits in the tree
     ;; changes nothing about what it covers, and being outside the render tree is
     ;; the point — the instrument no longer draws inside what it inspects.
     ;; Renders nothing at all while the mode is off.
     [xray/overlay]
     ;; The guide's coach mark — chrome like the top bar and the x-ray rings:
     ;; fixed, a sibling of the render tree, and nothing unless the island
     ;; names a guide its own tree does not draw (C-20, lane RK).
     [coach-overlay]
     ;; The Ctrl+K palette (row C-80) — chrome over the island, like xray's
     ;; overlay: fixed, on the named layers, drawing nothing while the
     ;; session's :command-palette-open says closed.
     [command-palette]
     (cond
       (and (= status :attached) render-tree)
       (let [gi? (= "gi" (island-from-url))]
         (if gi?
           ;; gi: the conversation grows downward and used to push the prompt
           ;; bar off the viewport with no scroll to reach it (George, 2026-07-22).
           ;; Wrap render-tree + input-bar in a bounded flex column and force
           ;; min-h-0 onto the render-tree wrapper so the conversation list
           ;; (its own overflow-y-auto) scrolls INSTEAD of growing the box —
           ;; the input bar stays pinned at the bottom, always reachable.
           ;; This min-h-0 is gi-only on purpose: render.cljs's root min-h-0
           ;; regressed the notes/vault content pane to nothing (its own
           ;; comment), so the non-gi path keeps the grow-to-content fragment.
           [:div.flex-1.min-h-0.flex.flex-col.overflow-hidden
            [:div.flex-1.min-h-0.flex.flex-col.overflow-hidden
             {:class "[&>div]:min-h-0"}
             [render/render-tree render-tree bus session-id (instrument-from-url)]]
            [input-bar]]
           [:<>
            [render/render-tree render-tree bus session-id (instrument-from-url)]
            ;; The prompt bar is a gi-agent affordance (send a message, save the
            ;; conversation). It is NOT island chrome — on notes/arch-explorer/studio
            ;; it was a leftover from when gi was the only island. Show it only on gi.
            (when gi? [input-bar])]))

       (= status :error)
       [:div.flex-1.flex.items-center.justify-center
        [:div.text-center
         [:p.ikc-fg-crit.text-lg "Failed to connect"]
         [:p.text-sm.ikc-fg-faint.mt-2 (or error "unknown error")]
         [:button.text-sm.ikc-fg-accent.mt-4.underline
          {:on-click #(connect-and-attach!)} "retry"]]]

       (= status :disconnected)
       [:div.flex-1.flex.items-center.justify-center
        [:div.text-center
         [:p.ikc-fg-muted.text-lg "Disconnected"]
         [:button.text-sm.ikc-fg-accent.mt-4.underline
          {:on-click #(connect-and-attach!)} "reconnect"]]]

       ;; THIS ARM MUST NAME THE STATE IT IS ACTUALLY IN, and it has now been
       ;; wrong twice for the same reason: a catch-all reports the FIRST state
       ;; it covers unless it is made to say otherwise.
       ;;
       ;; 2026-08-07..11: :status reached :attached with a nil render tree and
       ;; the screen said "Connecting to bus…". It accused the socket, which
       ;; was fine, and four days went into the transport. That arm was added.
       ;;
       ;; 2026-09-01, the SAME sentence again, one state further along. The
       ;; socket was open and the :ui/attach was simply slow — 10.2 s for
       ;; reader-v2, 13.4 s for conversations — and the screen still said
       ;; "Connecting to bus…" while a line under it read "status :connected".
       ;; The banner contradicted its own subtitle. George reported the island
       ;; as broken, which is the only thing the screen let him conclude.
       ;;
       ;; SO EVERY STATE IS NAMED HERE, and the wait is a MOVING NUMBER. A
       ;; motionless sentence cannot tell a slow island from a hung one; a
       ;; second counter can, and it needs no threshold to be chosen.
       :else
       (let [{:keys [headline detail warn]}
             (waiting-banner status (island-from-url) @waiting)]
         [:div.flex-1.flex.items-center.justify-center
          [:div.text-center
           [:p.ikc-fg-muted.text-lg headline]
           [:p.text-sm.ikc-fg-border.mt-2
            (if (= status :connected)
              detail
              (str "ws: " (origin/page-bus-url) " · " detail))]
           (when warn [:p.text-sm.ikc-fg-warn.mt-2 warn])]]))]))

;; ---------------------------------------------------------------------------
;; Error boundary — PLAN-graph-ui-v0-003-01: found 2026-07-08, a real render
;; crash (React's own "Invalid arity: 8" inside reagent's internals, cause
;; still unidentified after reading reagent's actual source and checking
;; every widget's own call-site/definition arity by hand) took the WHOLE app
;; down to a blank screen with no error boundary at all. This is a permanent
;; safety net, not specific to that one bug: any future render exception now
;; (a) dumps the exact :sources/:render-tree that were live at crash time —
;; the data this bug needed but a bare stack trace couldn't give — and
;; (b) shows a recoverable message instead of a blank screen.
;; ---------------------------------------------------------------------------

(defonce ^:private caught-error (r/atom nil))

(def ^:private error-boundary
  (r/create-class
    {:display-name "error-boundary"
     :component-did-catch
     (fn [_this error _info]
       (js/console.error "══ RENDER CRASHED ══ dumping last live app-state for diagnosis:")
       (js/console.error "sources:" (pr-str (:sources @app-state)))
       (js/console.error "render-tree:" (pr-str (:render-tree @app-state)))
       (js/console.error "widget-tree:" (pr-str (:widget-tree @app-state)))
       (js/console.error "the error object itself:" error)
       ;; THE ERROR IS KEPT FOR A REPORT (PLAN-ikc-v3-039-01 §2). The console
       ;; dump stays, and Report this files the same fact as a :fault-report.
       (support/note-error! error)
       (reset! caught-error error))
     :reagent-render
     (fn [child]
       (if @caught-error
         [:div.p-4.ikc-fg-crit
          [:p.font-semibold "Render crashed."]
          [:p.text-sm.ikc-fg-muted.mt-1
           "Press Report this to file a fault report. It collects the page, the error and your last steps for you."]
          ;; REPORT THIS, not "open the console" (PLAN-ikc-v3-039-01 §2). A
          ;; person who is not a developer has no console to open. The state
          ;; is still dumped there for one who does.
          [:button.text-sm.ikc-fg-accent.mt-3.underline.mr-4
           {:data-import-id "crash-report-this"
            :on-click #(support/report-fault!
                         {:bus        (:bus @app-state)
                          :session-id (:session-id @app-state)
                          :island     (island-from-url)
                          :crash      @caught-error})}
           "Report this"]
          [:button.text-sm.ikc-fg-accent.mt-3.underline
           {:on-click #(reset! caught-error nil)} "try again"]]
         child))}))

;; ---------------------------------------------------------------------------
;; Entry point.
;; ---------------------------------------------------------------------------

(defn- root
  "Root component: MantineProvider wraps the error boundary + app so every
   widget uses Mantine's design system (ADR-graph-ui-v0-012-01). `forceColorScheme`
   follows the active IKC theme."
  []
  [:> MantineProvider
   {:theme (theme/mantine-theme)
    :forceColorScheme @theme/mantine-scheme}
   [error-boundary [app]]
   ;; THE REPORT DIALOG, BESIDE THE BOUNDARY AND NOT INSIDE IT. "Report this"
   ;; on the crash pane opens it, so a crash must not take it away.
   [support/report-dialog]
   ;; IKC3/PLAN-ikc-v3-080-01/REPORT: the Reopen dialog of screen 1b.
   [reopen/reopen-dialog]])

(defn mount!
  "Mount the app into the #app DOM node. Called on initial load and on
   shadow-cljs hot-reload (see :after-load in shadow-cljs.edn)."
  []
  (rdom/render [root] (.getElementById js/document "app")))

(defn ^:export init
  "shadow-cljs entry point. Called when the :main module loads."
  []
  (theme/init!)
  ;; AFTER theme/init!, and that order is the whole of it: the breakpoint is a
  ;; theme token, so nothing can read it off the root before the theme has
  ;; written it (PLAN-ikc-v3-015-01 phase 6).
  (responsive/install!)
  ;; THE TWO ERROR HANDLERS THAT DID NOT EXIST (PLAN-ikc-v3-039-01 §2). Early,
  ;; so an error during the connection is kept for a report too.
  (support/install!)
  ;; NAME THIS TAB BEFORE ANYTHING ELSE HAPPENS (George, 2026-09-07: every
  ;; island "carried the same favicon and name in the tab"). It needs only the
  ;; URL, so it runs ahead of the socket: a tab that is still connecting, and
  ;; one that fails to connect, are both still identifiable. index.html's
  ;; static `<title>ikc3 — notes</title>` is what this replaces.
  (tab/identify! (island-from-url))
  (mount!)
  (try
    (connect-and-attach!)
    (catch js/Error e
      (js/console.error "init: connect-and-attach! failed:" e)
      (swap! app-state assoc :status :error
             :error (str "init error: " (.-message e))))))
